import * as THREE from 'three';

/**
 * Canvas-2D HUD — "OPERATION BLACKOUT" tactical interface.
 *
 * Everything here is drawn from vector primitives; there is not a single
 * system font call. The typeface (`VF` below) is a condensed technical
 * stencil face defined as polylines on a unit em-box, which gives us a
 * genuinely military numeral at any size, on any machine, with zero assets.
 *
 * Layout follows modern CoD composition:
 *   top-left     minimap + killstreak rail
 *   top-centre   compass ruler + objective banner
 *   top-right    killfeed
 *   bottom-left  vitals + equipment slots
 *   bottom-right ammo block
 *   centre       crosshair / hitmarkers / damage ring
 */

/* ============================================================ typography == */

/* Unit em-box: x 0..1, y 0..1 (y down, 0 = cap height, 1 = baseline).
 * `w` is the advance width in em units. Strokes are drawn with miter joins
 * so the face reads as cut metal rather than a rounded UI font.            */
const VF = {
  /* Slashed zero — the single most important disambiguation on an ammo counter. */
  '0': { w: .58, p: [[[.22,0],[.78,0],[1,.22],[1,.78],[.78,1],[.22,1],[0,.78],[0,.22],[.22,0]], [[.82,.14],[.18,.86]]] },
  '1': { w: .38, p: [[[0,.26],[.55,0],[.55,1]]] },
  '2': { w: .60, p: [[[0,.2],[.2,0],[.8,0],[1,.2],[1,.36],[.05,1],[1,1]]] },
  '3': { w: .60, p: [[[0,0],[.8,0],[1,.2],[1,.32],[.8,.47],[.32,.47]], [[.8,.47],[1,.63],[1,.8],[.8,1],[0,1]]] },
  '4': { w: .62, p: [[[.72,1],[.72,0],[0,.72],[1,.72]]] },
  '5': { w: .60, p: [[[1,0],[.06,0],[0,.44],[.78,.44],[1,.62],[1,.8],[.8,1],[0,1]]] },
  '6': { w: .60, p: [[[.92,.06],[.76,0],[.2,0],[0,.22],[0,.8],[.2,1],[.8,1],[1,.8],[1,.66],[.8,.5],[0,.5]]] },
  '7': { w: .58, p: [[[0,0],[1,0],[.32,1]]] },
  '8': { w: .60, p: [[[.2,0],[.8,0],[1,.18],[1,.31],[.8,.47],[.2,.47],[0,.31],[0,.18],[.2,0]], [[.2,.47],[.8,.47],[1,.64],[1,.82],[.8,1],[.2,1],[0,.82],[0,.64],[.2,.47]]] },
  '9': { w: .60, p: [[[.08,.94],[.24,1],[.8,1],[1,.78],[1,.2],[.8,0],[.2,0],[0,.2],[0,.34],[.2,.5],[1,.5]]] },
  'A': { w: .62, p: [[[0,1],[.5,0],[1,1]], [[.16,.66],[.84,.66]]] },
  'B': { w: .60, p: [[[0,0],[.76,0],[1,.2],[1,.31],[.8,.47],[0,.47]], [[0,.47],[.8,.47],[1,.64],[1,.8],[.78,1],[0,1],[0,0]]] },
  'C': { w: .60, p: [[[1,.2],[.8,0],[.2,0],[0,.2],[0,.8],[.2,1],[.8,1],[1,.8]]] },
  'D': { w: .60, p: [[[0,0],[.74,0],[1,.26],[1,.74],[.74,1],[0,1],[0,0]]] },
  'E': { w: .54, p: [[[1,0],[0,0],[0,1],[1,1]], [[0,.47],[.78,.47]]] },
  'F': { w: .52, p: [[[1,0],[0,0],[0,1]], [[0,.47],[.76,.47]]] },
  'G': { w: .62, p: [[[1,.2],[.8,0],[.2,0],[0,.2],[0,.8],[.2,1],[.8,1],[1,.8],[1,.54],[.54,.54]]] },
  'H': { w: .60, p: [[[0,0],[0,1]], [[1,0],[1,1]], [[0,.5],[1,.5]]] },
  'I': { w: .22, p: [[[.5,0],[.5,1]]] },
  'J': { w: .54, p: [[[1,0],[1,.8],[.8,1],[.2,1],[0,.8],[0,.66]]] },
  'K': { w: .58, p: [[[0,0],[0,1]], [[1,0],[.04,.56]], [[.34,.36],[1,1]]] },
  'L': { w: .50, p: [[[0,0],[0,1],[1,1]]] },
  'M': { w: .72, p: [[[0,1],[0,0],[.5,.56],[1,0],[1,1]]] },
  'N': { w: .62, p: [[[0,1],[0,0],[1,1],[1,0]]] },
  'O': { w: .62, p: [[[.2,0],[.8,0],[1,.2],[1,.8],[.8,1],[.2,1],[0,.8],[0,.2],[.2,0]]] },
  'P': { w: .58, p: [[[0,1],[0,0],[.78,0],[1,.2],[1,.38],[.78,.57],[0,.57]]] },
  'Q': { w: .62, p: [[[.2,0],[.8,0],[1,.2],[1,.8],[.8,1],[.2,1],[0,.8],[0,.2],[.2,0]], [[.58,.68],[1,1.1]]] },
  'R': { w: .60, p: [[[0,1],[0,0],[.78,0],[1,.2],[1,.38],[.78,.57],[0,.57]], [[.44,.57],[1,1]]] },
  'S': { w: .58, p: [[[1,.18],[.8,0],[.2,0],[0,.2],[0,.31],[.2,.46],[.8,.46],[1,.63],[1,.8],[.8,1],[.2,1],[0,.82]]] },
  'T': { w: .58, p: [[[0,0],[1,0]], [[.5,0],[.5,1]]] },
  'U': { w: .60, p: [[[0,0],[0,.8],[.2,1],[.8,1],[1,.8],[1,0]]] },
  'V': { w: .60, p: [[[0,0],[.5,1],[1,0]]] },
  'W': { w: .80, p: [[[0,0],[.18,1],[.5,.38],[.82,1],[1,0]]] },
  'X': { w: .58, p: [[[0,0],[1,1]], [[1,0],[0,1]]] },
  'Y': { w: .58, p: [[[0,0],[.5,.5],[1,0]], [[.5,.5],[.5,1]]] },
  'Z': { w: .56, p: [[[0,0],[1,0],[0,1],[1,1]]] },
  '/': { w: .42, p: [[[.86,-.04],[.06,1.04]]] },
  '\\':{ w: .42, p: [[[.06,-.04],[.86,1.04]]] },
  '-': { w: .40, p: [[[.06,.54],[.84,.54]]] },
  '+': { w: .46, p: [[[.06,.54],[.84,.54]], [[.45,.2],[.45,.88]]] },
  '.': { w: .22, p: [[[.5,.93],[.5,1]]] },
  ',': { w: .22, p: [[[.6,.9],[.2,1.16]]] },
  ':': { w: .20, p: [[[.5,.2],[.5,.29]], [[.5,.86],[.5,.95]]] },
  '!': { w: .22, p: [[[.5,0],[.5,.66]], [[.5,.92],[.5,1]]] },
  '%': { w: .70, p: [[[.9,0],[.1,1]], [[.02,.06],[.28,.06],[.28,.32],[.02,.32],[.02,.06]], [[.72,.68],[.98,.68],[.98,.94],[.72,.94],[.72,.68]]] },
  '(': { w: .30, p: [[[.7,-.04],[.16,.3],[.16,.7],[.7,1.04]]] },
  ')': { w: .30, p: [[[.1,-.04],[.64,.3],[.64,.7],[.1,1.04]]] },
  '[': { w: .30, p: [[[.66,-.02],[.16,-.02],[.16,1.02],[.66,1.02]]] },
  ']': { w: .30, p: [[[.14,-.02],[.64,-.02],[.64,1.02],[.14,1.02]]] },
  '<': { w: .42, p: [[[.76,.08],[.12,.5],[.76,.92]]] },
  '>': { w: .42, p: [[[.1,.08],[.74,.5],[.1,.92]]] },
  '#': { w: .66, p: [[[.26,0],[.14,1]], [[.62,0],[.5,1]], [[.02,.32],[.78,.32]], [[.02,.68],[.78,.68]]] },
  '*': { w: .46, p: [[[.44,.06],[.44,.7]], [[.14,.22],[.74,.56]], [[.74,.22],[.14,.56]]] },
  '_': { w: .50, p: [[[0,1.06],[1,1.06]]] },
  ' ': { w: .34, p: [] },
};

/** Measured advance of `str` at cap-height `size` with `track` px tracking. */
function vwidth(str, size, track) {
  let x = 0;
  for (let i = 0; i < str.length; i++) {
    const g = VF[str[i]] || VF[' '];
    x += g.w * size + (i < str.length - 1 ? track : 0);
  }
  return x;
}

/**
 * Draw vector text. `y` is the baseline (bottom of the caps).
 * Always renders a dark contour pass first so the face survives a blown-out
 * sky and a black road without needing a heavy drop shadow.
 */
export function vtext(ctx, str, x, y, size, o = {}) {
  str = String(str);
  const track = o.track ?? size * 0.16;
  const weight = (o.weight ?? 0.115) * size;
  const total = vwidth(str, size, track);
  let px = x;
  if (o.align === 'center') px -= total / 2;
  else if (o.align === 'right') px -= total;

  ctx.save();
  ctx.lineJoin = 'miter';
  ctx.miterLimit = 3;
  ctx.lineCap = o.cap || 'butt';

  const passes = o.contour === false
    ? [[o.color || '#fff', weight]]
    : [[o.contourColor || 'rgba(2,5,9,0.66)', weight + Math.max(1.5, size * 0.11)],
       [o.color || '#fff', weight]];

  for (const [col, lw] of passes) {
    ctx.strokeStyle = col;
    ctx.lineWidth = lw;
    ctx.beginPath();
    let cx2 = px;
    for (let i = 0; i < str.length; i++) {
      const g = VF[str[i]] || VF[' '];
      // Glyph outlines are authored on a full unit box; the advance `w`
      // condenses them horizontally, which is what gives the face its
      // narrow technical proportion.
      const gw = g.w * size;
      for (const poly of g.p) {
        for (let j = 0; j < poly.length; j++) {
          const gx = cx2 + poly[j][0] * gw;
          const gy = y - size + poly[j][1] * size;
          if (j === 0) ctx.moveTo(gx, gy); else ctx.lineTo(gx, gy);
        }
      }
      cx2 += gw + track;
    }
    ctx.stroke();
  }
  ctx.restore();
  return total;
}

/* ================================================================ helpers == */

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
/** Frame-rate independent exponential approach. */
export const damp = (a, b, l, dt) => lerp(a, b, 1 - Math.exp(-l * dt));
export const easeOut = (t) => 1 - Math.pow(1 - clamp(t, 0, 1), 3);
const easeIn = (t) => Math.pow(clamp(t, 0, 1), 2.2);
const easeBack = (t) => { t = clamp(t, 0, 1); const c = 1.9; return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2); };

/** Rectangle with cut (chamfered) corners — the tactical-panel silhouette. */
export function chamfer(ctx, x, y, w, h, c, corners = 0b1111) {
  ctx.beginPath();
  const tl = (corners & 1) ? c : 0, tr = (corners & 2) ? c : 0;
  const br = (corners & 4) ? c : 0, bl = (corners & 8) ? c : 0;
  ctx.moveTo(x + tl, y);
  ctx.lineTo(x + w - tr, y); ctx.lineTo(x + w, y + tr);
  ctx.lineTo(x + w, y + h - br); ctx.lineTo(x + w - br, y + h);
  ctx.lineTo(x + bl, y + h); ctx.lineTo(x, y + h - bl);
  ctx.lineTo(x, y + tl); ctx.closePath();
}

/** L-shaped corner brackets — cheap way to make a panel feel like an overlay. */
export function bracket(ctx, x, y, w, h, len, col, lw) {
  ctx.strokeStyle = col; ctx.lineWidth = lw; ctx.lineCap = 'butt';
  const c = [[x, y, 1, 1], [x + w, y, -1, 1], [x + w, y + h, -1, -1], [x, y + h, 1, -1]];
  ctx.beginPath();
  for (const [px, py, sx, sy] of c) {
    ctx.moveTo(px + sx * len, py); ctx.lineTo(px, py); ctx.lineTo(px, py + sy * len);
  }
  ctx.stroke();
}

export const CO = {
  ink: 'rgba(3,6,10,0.60)',
  hi: 'rgba(233,242,250,0.97)',
  dim: 'rgba(178,194,210,0.9)',
  faint: 'rgba(168,184,200,0.78)',
  accent: 'rgba(255,196,74,1)',
  friend: 'rgba(112,206,255,1)',
  enemy: 'rgba(255,98,78,1)',
  good: 'rgba(126,222,140,1)',
  warn: 'rgba(255,176,32,1)',
};

/* ==================================================================== HUD == */

export class HUD {
  constructor(container) {
    this.canvas = document.createElement('canvas');
    Object.assign(this.canvas.style, {
      position: 'fixed', inset: '0', width: '100%', height: '100%',
      pointerEvents: 'none', zIndex: '10',
    });
    container.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');

    /* ---- public contract ---- */
    this.kills = 0;
    this.score = 0;

    /* ---- transient state ---- */
    this.time = 0;
    this.boot = 0;                 // 0..1 entrance master
    this.hitmarker = 0;            // remaining life
    this.hitmarkerDur = 0.3;
    this.hitKind = 'body';         // body | armor | head | kill | multi
    this.hitCombo = 0;
    this.hitComboTimer = 0;
    this.killfeed = [];
    this.hitDirs = [];
    this.damageIndicators = [];    // legacy field, kept for compatibility

    this.hpGhost = 1;              // chip-damage trailing bar
    this.hpGhostHold = 0;
    this.hpNow = 1;
    this.hurtFlash = 0;
    this.lowPulse = 0;
    this.regenT = 0;

    this.ammoPrev = -1;
    this.ammoFlash = 0;
    this.ammoRoll = 0;
    this.lowAmmoPulse = 0;
    this.reloadT = 0;
    this.wasReloading = false;

    this.spreadEase = 0;
    this.bloom = 0;
    this.enemyAim = 0;
    this.adsEase = 0;

    this.streakToast = null;
    this.objective = null;
    this.scoreboardEase = 0;

    this._mapRects = null;
    this._mapTry = 0;

    this.showObjective('SECURE THE SECTOR', 'ELIMINATE ALL HOSTILES', 5.5);

    this.resize();
    addEventListener('resize', () => this.resize());
  }

  resize() {
    const dpr = Math.min(devicePixelRatio, 2);
    this.w = innerWidth;
    this.h = innerHeight;
    this.canvas.width = Math.max(1, this.w * dpr);
    this.canvas.height = Math.max(1, this.h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.s = clamp(Math.min(this.w / 1920, this.h / 1080), 0.62, 1.6);
  }

  /* ------------------------------------------------------------- events -- */

  /**
   * @param {boolean|string} kind  legacy boolean (isKill) or one of
   *        'body' | 'armor' | 'head' | 'kill' | 'multi'
   */
  hit(kind = false) {
    if (kind === true) kind = 'kill';
    else if (kind === false) kind = 'body';
    if (kind === 'kill' && this.hitComboTimer > 0) { this.hitCombo++; kind = 'multi'; }
    else if (kind === 'kill') { this.hitCombo = 1; }
    if (kind === 'kill' || kind === 'multi') this.hitComboTimer = 2.4;

    this.hitKind = kind;
    this.hitmarkerDur = kind === 'multi' ? 0.62 : kind === 'kill' ? 0.5 : kind === 'head' ? 0.4 : 0.3;
    this.hitmarker = this.hitmarkerDur;
    this.hitmarkerKill = kind === 'kill' || kind === 'multi';   // legacy read
  }

  /** `friendly` marks a team kill: it scores nothing and reads red in the feed. */
  addKill(name = 'ENEMY', weapon = 'M4A1', friendly = false) {
    if (!friendly) {
      this.kills++;
      this.score += 100;
    } else {
      this.score = Math.max(0, this.score - 100);
    }
    for (const k of this.killfeed) k.slot++;
    this.killfeed.unshift({
      actor: 'YOU', victim: String(name).toUpperCase(),
      weapon: String(weapon).toUpperCase(), life: 6, age: 0, slot: 0, friendly,
    });
    if (this.killfeed.length > 5) this.killfeed.pop();

    if (friendly) return;                 // no streak progress for a team kill
    const s = STREAKS.find((x) => x.at === this.kills);
    if (s) this.streakToast = { name: s.name, sub: 'KILLSTREAK READY', t: 0 };
  }

  damageFrom(angleRad) {
    // Stack onto a nearby indicator instead of overdrawing it.
    for (const d of this.hitDirs) {
      if (Math.abs(Math.atan2(Math.sin(d.angle - angleRad), Math.cos(d.angle - angleRad))) < 0.42) {
        d.life = 1.25; d.stack = Math.min(4, (d.stack || 1) + 1); d.pop = 1;
        this.hurtFlash = 1; return;
      }
    }
    this.hitDirs.push({ angle: angleRad, life: 1.25, stack: 1, pop: 1 });
    if (this.hitDirs.length > 6) this.hitDirs.shift();
    this.hurtFlash = 1;
  }

  showObjective(title, sub, dur = 5) {
    this.objective = { title: String(title).toUpperCase(), sub: String(sub).toUpperCase(), t: 0, dur };
  }

  /* ------------------------------------------------------------ minimap -- */

  /** Level footprints, harvested once and cached. */
  _mapData(state) {
    if (this._mapRects) return this._mapRects;
    if (this._mapTry++ > 240) return null;
    const level = state.level ?? (typeof window !== 'undefined' && window.__game?.game?.level);
    if (!level || !level.collision) return null;
    const rects = [];
    const bb = new THREE.Box3();
    const all = new THREE.Box3();
    level.collision.updateWorldMatrix(true, true);
    all.setFromObject(level.collision);
    const mapW = Math.max(1, all.max.x - all.min.x);
    const mapD = Math.max(1, all.max.z - all.min.z);
    level.collision.traverse((o) => {
      if (!o.isMesh || o.isInstancedMesh) return;
      bb.setFromObject(o);
      const sx = bb.max.x - bb.min.x, sy = bb.max.y - bb.min.y, sz = bb.max.z - bb.min.z;
      // Reject anything that blankets the level, measured against the level's
      // own extent rather than a fixed 120 m. A BSP map merges its geometry
      // into a few buckets whose AABBs each span the whole map; at 120 m those
      // slipped through on Dust II and painted the minimap a solid white slab.
      if (sx > mapW * 0.6 && sz > mapD * 0.6) return;
      if (sx > 120 || sz > 120) return;
      if (sx < 0.5 && sz < 0.5) return;               // clutter
      rects.push({
        x: bb.min.x, z: bb.min.z, w: sx, d: sz,
        tall: sy > 2.4 && bb.max.y > 2.0,
      });
    });
    this._mapRects = rects.length ? rects : null;
    return this._mapRects;
  }

  /** Enemy blips. Pooled — this runs every frame during combat. */
  _blips(state) {
    if (state.enemies) return state.enemies;
    const g = typeof window !== 'undefined' && window.__game?.game;
    if (!g || !g.enemies) return null;
    const pool = this._blipPool || (this._blipPool = []);
    const list = this._blipList || (this._blipList = []);
    list.length = 0;
    for (let i = 0; i < g.enemies.length; i++) {
      const e = g.enemies[i];
      const b = pool[i] || (pool[i] = { x: 0, z: 0, yaw: 0, dead: false, hot: false });
      b.x = e.root.position.x; b.z = e.root.position.z; b.yaw = e.yaw;
      b.dead = e.state === 'dead'; b.hot = e.state === 'engage';
      list.push(b);
    }
    return list;
  }

  _playerPos(state) {
    if (state.pos) return state.pos;
    const g = typeof window !== 'undefined' && window.__game?.game;
    return g?.player?.position || null;
  }

  /* --------------------------------------------------------------- draw -- */

  draw(dt, state) {
    dt = Math.min(dt || 0, 0.05);
    const ctx = this.ctx;
    const { w, h, s } = this;
    this.time += dt;
    ctx.clearRect(0, 0, w, h);

    this._drawTelemetry(ctx, state);

    const paused = !!state.showPrompt;
    this.boot = paused ? 0 : Math.min(1, this.boot + dt / 0.75);
    const B = easeOut(this.boot);

    /* Shared eases */
    const ads = state.ads ?? 0;
    this.adsEase = damp(this.adsEase, ads, 18, dt);
    this.spreadEase = damp(this.spreadEase, state.spread ?? 0.01, 14, dt);
    this.bloom = Math.max(0, this.bloom - dt * 4.5);
    this.hurtFlash = Math.max(0, this.hurtFlash - dt * 1.6);
    this.ammoFlash = Math.max(0, this.ammoFlash - dt * 7);
    this.ammoRoll = damp(this.ammoRoll, 0, 22, dt);
    this.hitComboTimer = Math.max(0, this.hitComboTimer - dt);
    if (this.hitComboTimer === 0) this.hitCombo = 0;

    const mag = state.ammo ?? 0;
    if (this.ammoPrev < 0) this.ammoPrev = mag;
    if (mag < this.ammoPrev) { this.ammoFlash = 1; this.bloom = 1; this.ammoRoll = 1; }
    else if (mag > this.ammoPrev) { this.ammoFlash = 0.7; this.ammoRoll = -1; }
    this.ammoPrev = mag;

    const hp = clamp((state.health ?? 100) / (state.maxHealth ?? 100), 0, 1);
    if (hp < this.hpNow - 0.001) { this.hpGhostHold = 0.45; this.regenT = 0; }
    else this.regenT += dt;
    this.hpNow = damp(this.hpNow, hp, 22, dt);
    if (this.hpGhostHold > 0) this.hpGhostHold -= dt;
    else this.hpGhost = damp(this.hpGhost, this.hpNow, 4.5, dt);
    if (this.hpGhost < this.hpNow) this.hpGhost = this.hpNow;

    /* ---------------------------------------------------- screen effects */
    // Not while spectating.
    //
    // The vignette is driven by low health, and a spectator is on zero — so it
    // pinned at full strength and pulsed a heavy red wash over the whole screen
    // for as long as you were dead. In deathmatch that is three seconds and
    // reads as dying. In bomb defusal it is the rest of the round, several
    // minutes of watching your team through a blood filter. You are not hurt at
    // that point, you are gone.
    this._drawHurtEdges(ctx, state.spectating ? 1 : hp);

    if (!paused) {
      this._drawMinimap(ctx, state, B);
      this._drawCompass(ctx, state, B);
      this._drawKillfeed(ctx, dt, B);
      this._drawVitals(ctx, state, hp, B, dt);
      this._drawEquipment(ctx, state, B);
      // No ammo readout while spectating: the numbers are the DEAD player's
      // gun, and reading a live magazine count off a corpse is worse than
      // showing nothing.
      if (!state.spectating) this._drawAmmo(ctx, state, mag, B);
      this._drawStreaks(ctx, dt, B, state);
      this._drawObjective(ctx, dt);
      // No reticle either: it is aiming a gun you are not holding, at a
      // crosshair that belongs to the camera rather than to anyone's weapon.
      if (!state.spectating) {
        this._drawCrosshair(ctx, state, dt);
        this._drawHitmarker(ctx, dt);
        this._drawDamageRing(ctx, dt);
      }
      this._drawScoreboard(ctx, state, dt);
      this._drawUsePrompt(ctx, state, dt);
      this._drawProgress(ctx, state);
      this._drawBombFuse(ctx, state, dt);
      this._drawRoundBar(ctx, state, dt);
      this._drawBombCarry(ctx, state, dt);
      this._drawBombMarker(ctx, state, dt);
      this._drawSpectator(ctx, state, dt);
      this._drawRoundResult(ctx, state, dt);
    }

    // Over everything: a decided match is not something to peer past.
    state.matchAwards?.draw(ctx, this.w, this.h, this.s);

    if (state.showPrompt) this._drawPrompt(ctx);
  }

  /**
   * The round result: who won, and why.
   *
   * A round ending is the single most important thing that happens in bomb
   * defusal, and it was reported by a banner the same size as "RELOADING". This
   * takes the screen for the few seconds between rounds — which is dead time
   * anyway, since movement is locked — and names the winning SIDE rather than a
   * colour, because "attackers won" is what a player actually needs to know.
   */
  _drawRoundResult(ctx, state, dt) {
    // The award screens supersede this: they say the same thing at ten times
    // the size, and stacking the two puts "ATTACKERS WIN" on screen twice.
    if (state.matchAwards) { this._resultEase = 0; return; }
    const over = state.roundPhase === 'over' || state.roundPhase === 'match';
    const winner = state.roundWinner;
    this._resultEase = damp(this._resultEase ?? 0, over && winner ? 1 : 0, 9, dt);
    if (this._resultEase < 0.01) return;

    const { w, h, s } = this;
    const e = easeOut(this._resultEase);
    const mine = state.teamScore?.mine ?? null;
    // Once the match is decided the screen is about the MATCH, not the round
    // that happened to end it — and those differ, because a match can finish on
    // the round cap with the last round going to the side that lost overall.
    const mw = state.matchWinner ?? null;
    const draw = mw === 'draw';
    const subject = mw && !draw ? mw : winner;
    const won = !!mine && subject === mine;
    const match = !!mw;

    ctx.save();
    ctx.globalAlpha = e;

    // A band rather than a full wash: the round is over but the world behind it
    // is still worth watching, and a defuse you lost by half a second is worth
    // seeing happen.
    const bandH = 190 * s;
    const by = h / 2 - bandH / 2 - 30 * s;
    const g = ctx.createLinearGradient(0, by, 0, by + bandH);
    g.addColorStop(0, 'rgba(3,6,10,0)');
    g.addColorStop(0.5, 'rgba(3,6,10,0.78)');
    g.addColorStop(1, 'rgba(3,6,10,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, by, w, bandH);

    const side = subject === 'red' ? 'ATTACKERS' : 'DEFENDERS';
    const col = draw ? 'rgba(226,240,252,0.96)'
      : subject === 'red' ? 'rgba(255,120,96,0.98)' : 'rgba(120,180,255,0.98)';
    const cx = w / 2;
    const cy = by + bandH / 2;
    const lift = (1 - e) * 16 * s;

    // The round line carries "of ten" so the match has a visible shape: knowing
    // it is round 8 of 10 changes how you play it.
    const of = state.roundsPerMatch;
    const head = match ? 'MATCH OVER'
      : `ROUND ${state.round ?? 1}${of ? ` OF ${of}` : ''}`;
    vtext(ctx, head, cx, cy - 52 * s - lift, 11 * s,
      { color: CO.faint, weight: 0.17, track: 9 * s, align: 'center' });
    vtext(ctx, draw ? 'DRAW' : `${side} WIN`, cx, cy - 6 * s - lift, 38 * s,
      { color: col, weight: 0.12, track: 11 * s, align: 'center' });

    // Why it ended. The four bomb endings read very differently to the two
    // sides and the reason is most of the story.
    const why = {
      defused: 'BOMB DEFUSED', exploded: 'BOMB DETONATED',
      elimination: 'TEAM ELIMINATED', time: 'TIME EXPIRED', score: 'SCORE LIMIT',
    }[state.roundReason] ?? '';
    if (why) {
      vtext(ctx, why, cx, cy + 26 * s - lift, 13 * s,
        { color: CO.hi, weight: 0.15, track: 6 * s, align: 'center' });
    }

    // Where that leaves you, from your own side's point of view.
    if (mine) {
      const ts = state.teamScore ?? {};
      const ours = mine === 'red' ? ts.red : ts.blue;
      const theirs = mine === 'red' ? ts.blue : ts.red;
      vtext(ctx, `${ours} - ${theirs}`, cx, cy + 62 * s - lift, 20 * s,
        { color: draw ? CO.hi : won ? CO.friend : CO.enemy, weight: 0.13, track: 5 * s, align: 'center' });
      const tail = draw ? 'MATCH DRAWN'
        : match ? (won ? 'YOU WON THE MATCH' : 'YOU LOST THE MATCH')
          : (won ? 'ROUND WON' : 'ROUND LOST');
      vtext(ctx, tail, cx, cy + 82 * s - lift, 9.5 * s,
        { color: CO.faint, weight: 0.17, track: 4.4 * s, align: 'center' });
    }
    ctx.restore();
  }

  /**
   * Round clock and team score, top centre. Only in a round-based mode —
   * deathmatch has neither, and `decorateHud` leaves `roundPhase` unset there.
   */
  _drawRoundBar(ctx, state, dt) {
    if (!state.roundPhase) return;
    const { s } = this;
    const cx = this.w / 2;
    // Below the compass strip, not through it.
    const y = 92 * s;
    const mine = state.teamScore?.mine ?? 'blue';
    const red = state.teamScore?.red ?? 0;
    const blue = state.teamScore?.blue ?? 0;

    // Freeze time counts down to the round starting; a planted bomb is the
    // fuse's business, so the round clock stops mattering and says so.
    // Once planted the round clock stops mattering and the fuse is the only
    // number worth reading, so the bar shows that instead.
    const planted = state.roundPhase === 'planted';
    const t = planted
      ? Math.max(0, state.bombFuse ?? 0)
      : Math.max(0, state.roundClock ?? 0);
    const mm = Math.floor(t / 60);
    const ss = Math.floor(t % 60);
    const clock = planted ? `0:${String(Math.ceil(t)).padStart(2, '0')}`
      : `${mm}:${String(ss).padStart(2, '0')}`;

    ctx.save();
    const boxW = 168 * s;
    const boxH = 34 * s;
    chamfer(ctx, cx - boxW / 2, y - boxH / 2, boxW, boxH, 6 * s, 0b1111);
    ctx.fillStyle = 'rgba(4,8,12,0.52)';
    ctx.fill();

    // Low clock turns amber then red, so it is readable without being counted.
    const col = planted ? 'rgba(255,110,92,0.98)'
      : t <= 10 ? 'rgba(255,96,84,0.98)'
        : t <= 20 ? 'rgba(255,190,90,0.96)'
          : 'rgba(226,240,252,0.95)';
    vtext(ctx, clock, cx, y + 5 * s, 19 * s,
      { color: col, weight: 0.14, track: 3.4 * s, align: 'center' });

    // Your side's score sits on the left of the clock whichever side that is.
    const ours = mine === 'red' ? red : blue;
    const theirs = mine === 'red' ? blue : red;
    vtext(ctx, String(ours), cx - boxW / 2 - 16 * s, y + 5 * s, 17 * s,
      { color: CO.friend, weight: 0.14, track: 2.6 * s, align: 'center' });
    vtext(ctx, String(theirs), cx + boxW / 2 + 16 * s, y + 5 * s, 17 * s,
      { color: CO.enemy, weight: 0.14, track: 2.6 * s, align: 'center' });

    ctx.restore();

    // No BOMB PLANTED banner here on purpose — `_drawBombFuse` already draws
    // one with the countdown, and two of them stacked read as a bug.
  }

  /**
   * Frame rate, and online, the round trip and which room you are in.
   *
   * Bottom-right and deliberately quiet: this is diagnostic furniture, not part
   * of the game, and it should be legible when you look for it and invisible
   * when you do not. Ping is colour-coded because the number only matters when
   * it is bad.
   */
  _drawTelemetry(ctx, state) {
    const t = state.telemetry;
    if (!t) return;
    const { s } = this;
    const x = this.w - 14 * s;
    let y = this.h - 12 * s;

    ctx.save();
    if (t.room) {
      vtext(ctx, `ROOM ${String(t.room).toUpperCase()}`, x, y, 9 * s,
        { color: CO.faint, weight: 0.18, track: 2.6 * s, align: 'right' });
      y -= 13 * s;
    }
    if (t.ping != null) {
      const ms = Math.round(t.ping);
      // Under 60 is unremarkable, under 120 is playable, beyond that you feel it.
      const col = ms < 60 ? CO.faint : ms < 120 ? CO.hi : '#ff8f6b';
      vtext(ctx, `${ms} MS`, x, y, 10 * s,
        { color: col, weight: 0.16, track: 2.6 * s, align: 'right' });
      y -= 13 * s;
    }
    const fps = Math.round(t.fps);
    vtext(ctx, `${fps} FPS`, x, y, 10 * s,
      { color: fps >= 50 ? CO.faint : fps >= 30 ? CO.hi : '#ff8f6b',
        weight: 0.16, track: 2.6 * s, align: 'right' });
    ctx.restore();
  }

  /** Who you are watching while dead, and how to change it. */
  _drawSpectator(ctx, state, dt) {
    const sp = state.spectating;
    this._specFade = damp(this._specFade ?? 0, sp ? 1 : 0, 8, dt);
    if (this._specFade < 0.01) return;
    if (sp) this._specLast = sp;
    const info = sp ?? this._specLast;
    if (!info) return;

    const { s } = this;
    const cx = this.w / 2;
    const y = this.h - 130 * s;
    ctx.save();
    ctx.globalAlpha = this._specFade;

    vtext(ctx, 'SPECTATING', cx, y - 20 * s, 10 * s,
      { color: CO.faint, weight: 0.16, track: 5.5 * s, align: 'center' });
    vtext(ctx, String(info.name).toUpperCase(), cx, y + 6 * s, 20 * s,
      { color: CO.hi, weight: 0.13, track: 4 * s, align: 'center' });
    vtext(ctx, `${info.index} / ${info.count}   [FIRE] NEXT   [A/D] CYCLE`,
      cx, y + 28 * s, 9.5 * s,
      { color: CO.faint, weight: 0.16, track: 3 * s, align: 'center' });
    ctx.restore();
  }

  /** "YOU HAVE THE BOMB" badge, bottom centre, while you are the carrier. */
  _drawBombCarry(ctx, state, dt) {
    this._carryFade = damp(this._carryFade ?? 0, state.carryingBomb ? 1 : 0, 10, dt);
    if (this._carryFade < 0.01) return;
    const { s } = this;
    const cx = this.w / 2;
    const y = this.h - 96 * s;

    ctx.save();
    ctx.globalAlpha = this._carryFade;
    const bw = 186 * s;
    const bh = 30 * s;
    chamfer(ctx, cx - bw / 2, y - bh / 2, bw, bh, 6 * s, 0b1111);
    ctx.fillStyle = 'rgba(4,8,12,0.55)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,170,60,0.75)';
    ctx.lineWidth = 1.4 * s;
    ctx.stroke();

    // Slow pulse so it stays present without nagging.
    this._carryPulse = (this._carryPulse ?? 0) + dt * 2.4;
    const a = 0.78 + 0.22 * Math.sin(this._carryPulse);
    vtext(ctx, 'YOU HAVE THE BOMB', cx, y + 4.5 * s, 12 * s,
      { color: `rgba(255,196,96,${a.toFixed(3)})`, weight: 0.15, track: 3.6 * s, align: 'center' });
    ctx.restore();
  }

  /**
   * Screen marker for a loose or planted bomb, clamped to the edge when it is
   * off screen. A dropped bomb is a small dark object on dark ground — without
   * this it is effectively lost.
   */
  _drawBombMarker(ctx, state, dt) {
    const p = state.bombNDC;
    if (!p) return;
    const { s } = this;

    const onScreen = p.z > 0 && Math.abs(p.x) <= 1 && Math.abs(p.y) <= 1;
    let sx = (p.x * 0.5 + 0.5) * this.w;
    let sy = (-p.y * 0.5 + 0.5) * this.h;
    if (!onScreen) {
      // Behind or outside: clamp to a ring near the edge so it still points.
      const m = 70 * s;
      const dx = p.z > 0 ? p.x : -p.x;
      const dy = p.z > 0 ? p.y : -p.y;
      const len = Math.hypot(dx, dy) || 1;
      sx = this.w / 2 + (dx / len) * (this.w / 2 - m);
      sy = this.h / 2 - (dy / len) * (this.h / 2 - m);
    }

    const planted = state.bombState === 'planted';
    this._bombBlink = (this._bombBlink ?? 0) + dt * (planted ? 5 : 2.6);
    const a = 0.6 + 0.4 * Math.sin(this._bombBlink);
    const col = planted ? `rgba(255,104,88,${a.toFixed(3)})` : `rgba(255,196,96,${a.toFixed(3)})`;

    ctx.save();
    ctx.strokeStyle = col;
    ctx.lineWidth = 2 * s;
    const r = 9 * s;
    ctx.beginPath();
    ctx.moveTo(sx - r, sy); ctx.lineTo(sx, sy - r);
    ctx.lineTo(sx + r, sy); ctx.lineTo(sx, sy + r);
    ctx.closePath();
    ctx.stroke();
    if (state.bombMarker) {
      vtext(ctx, state.bombMarker, sx, sy + 22 * s, 10 * s,
        { color: col, weight: 0.16, track: 2.8 * s, align: 'center' });
    }
    ctx.restore();
  }

  /**
   * Contextual interaction prompt — "[E] USE" beside a button, "CLIMB" on a
   * ladder. Fades rather than popping, so it doesn't flicker while walking past.
   */
  _drawUsePrompt(ctx, state, dt) {
    const want = state.usePrompt ? 1 : 0;
    this._useFade = (this._useFade ?? 0) + (want - (this._useFade ?? 0)) * Math.min(1, dt * 12);
    if (this._useFade < 0.01) return;
    if (state.usePrompt) this._useLabel = state.usePrompt;

    const { s } = this;
    const cx = this.w / 2;
    const y = this.h / 2 + 88 * s;
    ctx.save();
    ctx.globalAlpha = this._useFade;

    const label = this._useLabel ?? 'USE';
    if (label === 'USE') {
      // Keycap + verb, matching the deploy screen's control chips.
      const kw = vwidth('E', 11 * s, 2.4 * s) + 18 * s;
      const tw = vwidth('USE', 12 * s, 3 * s);
      const total = kw + 10 * s + tw;
      const kx = cx - total / 2 + kw / 2;
      chamfer(ctx, kx - kw / 2, y - 15 * s, kw, 21 * s, 4 * s, 0b1111);
      ctx.fillStyle = 'rgba(255,255,255,0.07)'; ctx.fill();
      ctx.strokeStyle = 'rgba(200,216,232,0.28)'; ctx.lineWidth = 1 * s; ctx.stroke();
      vtext(ctx, 'E', kx, y, 11 * s, { color: CO.hi, weight: 0.15, track: 2.4 * s, align: 'center' });
      vtext(ctx, 'USE', kx + kw / 2 + 10 * s + tw / 2, y, 12 * s,
        { color: CO.accent, weight: 0.15, track: 3 * s, align: 'center' });
    } else {
      vtext(ctx, label, cx, y, 12 * s,
        { color: CO.hi, weight: 0.15, track: 3.5 * s, align: 'center' });
    }
    ctx.restore();
  }

  /**
   * Plant / defuse progress. A centred bar under the crosshair — the one thing
   * the HUD had no vocabulary for, since the use prompt was binary.
   */
  _drawProgress(ctx, state) {
    const pr = state.progress;
    if (!pr) return;
    const { s } = this;
    const w = 260 * s;
    const h = 7 * s;
    const x = this.w / 2 - w / 2;
    const y = this.h / 2 + 116 * s;
    const k = clamp(pr.t, 0, 1);

    ctx.save();
    ctx.fillStyle = 'rgba(6,10,14,0.72)';
    chamfer(ctx, x - 2 * s, y - 2 * s, w + 4 * s, h + 4 * s, 3 * s, 0b1111);
    ctx.fill();
    // Defusing is the tense one, so it gets the friendly blue; planting amber.
    const col = pr.kind === 'DEFUSE' ? '#5590e8' : '#ffb020';
    ctx.fillStyle = col;
    ctx.fillRect(x, y, w * k, h);
    ctx.strokeStyle = 'rgba(200,216,232,0.35)';
    ctx.lineWidth = 1 * s;
    ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
    vtext(ctx, `${pr.kind}…`, this.w / 2, y - 10 * s, 12 * s,
      { color: col, weight: 0.15, track: 4 * s, align: 'center' });
    ctx.restore();
  }

  /**
   * Bomb fuse. Pulses on the same accelerating cadence as the beep, so the
   * screen and the audio agree about how much time is left.
   */
  _drawBombFuse(ctx, state, dt) {
    if (state.bombFuse == null) return;
    const { s } = this;
    const t = Math.max(0, state.bombFuse);
    const urgency = 1 - Math.min(1, t / 40);
    this._fuseBlink = ((this._fuseBlink ?? 0) + dt * (2 + urgency * 12)) % 1;
    const hot = this._fuseBlink < 0.5;

    const x = this.w / 2;
    const y = 188 * s;          // clear of the objective banner at 138*s
    ctx.save();
    vtext(ctx, 'BOMB PLANTED', x, y, 13 * s,
      { color: hot ? '#ff4a32' : 'rgba(255,74,50,0.45)', weight: 0.16, track: 6 * s, align: 'center' });
    vtext(ctx, t.toFixed(1), x, y + 26 * s, 22 * s,
      { color: hot ? '#ffd0c4' : '#ff6a52', weight: 0.13, track: 3 * s, align: 'center' });
    ctx.restore();
  }

  /* ------------------------------------------------------------ effects -- */

  _drawHurtEdges(ctx, hp) {
    const { w, h } = this;
    const low = hp < 0.4 ? (0.4 - hp) / 0.4 : 0;
    this.lowPulse += 0.016;
    const amt = Math.max(this.hurtFlash * 0.9, low * (0.62 + 0.34 * Math.sin(this.lowPulse * 4.2)));
    if (amt <= 0.002) return;
    ctx.save();
    const g = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.22, w / 2, h / 2, Math.max(w, h) * 0.60);
    g.addColorStop(0, 'rgba(150,0,0,0)');
    g.addColorStop(0.55, `rgba(138,8,8,${0.26 * amt})`);
    g.addColorStop(1, `rgba(104,0,0,${0.88 * amt})`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    // Wet impact smears clinging to the frame edge — irregular, not a ring.
    ctx.globalAlpha = amt * 0.5;
    ctx.fillStyle = 'rgba(72,3,3,0.9)';
    for (let i = 0; i < 8; i++) {
      const t = (i / 8) * Math.PI * 2 + 0.7;
      const ex = w / 2 + Math.cos(t) * w * 0.52;
      const ey = h / 2 + Math.sin(t) * h * 0.56;
      const r = (28 + ((i * 37) % 60)) * this.s;
      const gg = ctx.createRadialGradient(ex, ey, 0, ex, ey, r);
      gg.addColorStop(0, 'rgba(88,4,4,0.55)');
      gg.addColorStop(1, 'rgba(88,4,4,0)');
      ctx.fillStyle = gg;
      ctx.beginPath(); ctx.arc(ex, ey, r, 0, 6.2832); ctx.fill();
    }
    ctx.restore();
  }

  /* ---------------------------------------------------------- crosshair -- */

  _drawCrosshair(ctx, state, dt) {
    const cx = this.w / 2, cy = this.h / 2, s = this.s;
    const ads = this.adsEase;
    const hostile = (state.aimAtEnemy ?? this.enemyAimFallback(state)) ? 1 : 0;
    this.enemyAim = damp(this.enemyAim, hostile, 16, dt);
    // Red is a warning, not a confirmation: it means "this is your own team".
    // Aiming at someone you are supposed to shoot leaves the reticle normal.
    this.friendAim = damp(this.friendAim ?? 0, state.aimAtFriend ? 1 : 0, 16, dt);

    const alpha = (1 - easeIn(ads / 0.85)) * (0.55 + 0.45 * easeOut(this.boot));
    if (alpha <= 0.01) return;

    const spread = this.spreadEase;
    const gap = (4 + spread * 1650 + this.bloom * 10) * s * lerp(1, 0.45, ads);
    const len = (12 - this.enemyAim * 2.5) * s * lerp(1, 0.55, ads);
    const lw = lerp(2.8, 1.5, ads) * s;

    const col = this.friendAim > 0.5
      ? `rgba(255,${(88 - this.friendAim * 46) | 0},${(72 - this.friendAim * 38) | 0},1)`
      : 'rgba(226,240,252,0.95)';

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.lineCap = 'butt';

    if ((state.pellets ?? 1) > 1) {
      this._drawPelletRing(ctx, state, cx, cy, col, lw, ads);
    } else {
      for (const pass of [['rgba(0,0,0,0.72)', lw + 3 * s], [col, lw]]) {
        ctx.strokeStyle = pass[0]; ctx.lineWidth = pass[1];
        ctx.beginPath();
        for (const [dx, dy] of [[0, -1], [0, 1], [-1, 0], [1, 0]]) {
          ctx.moveTo(cx + dx * gap, cy + dy * gap);
          ctx.lineTo(cx + dx * (gap + len), cy + dy * (gap + len));
        }
        ctx.stroke();
      }
    }

    // Centre dot: a hard pip in hip-fire, dissolves into the optic in ADS.
    ctx.globalAlpha = alpha * (1 - ads) * 0.95;
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(cx - 2.3 * s, cy - 2.3 * s, 4.6 * s, 4.6 * s);
    ctx.fillStyle = col;
    ctx.fillRect(cx - 1.3 * s, cy - 1.3 * s, 2.6 * s, 2.6 * s);

    /*
     * Hostile lock: corner ticks snapping in around the reticle.
     *
     * SHAPE, not colour. These used to be drawn in `CO.enemy`, which is
     * rgba(255,98,78) — red — and that flatly contradicts the rule stated
     * where `col` is chosen above: red is a WARNING meaning "this is your own
     * team", never a confirmation that you are on target. With red on both, the
     * one cue a player must never misread was carrying two opposite meanings,
     * and the brighter, snappier of the two was the one that said "shoot".
     *
     * The ticks are good feedback and they stay; they just read in the same
     * neutral tone as the rest of the reticle now, so the only red on this HUD
     * element means hold your fire.
     */
    if (this.enemyAim > 0.02) {
      const e = easeOut(this.enemyAim);
      const r = (gap + len + 8 * s) * lerp(1.5, 1, e);
      ctx.globalAlpha = alpha * e;
      ctx.strokeStyle = 'rgba(226,240,252,0.95)';
      ctx.lineWidth = 1.8 * s;
      const t = 5 * s;
      ctx.beginPath();
      for (const [sx, sy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
        ctx.moveTo(cx + sx * r, cy + sy * (r - t));
        ctx.lineTo(cx + sx * r, cy + sy * r);
        ctx.lineTo(cx + sx * (r - t), cy + sy * r);
      }
      ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * The shotgun reticle: four arcs on a ring, the way Call of Duty draws one.
   *
   * A shotgun does not point at a spot, it covers an area, and the four-tick
   * cross says the opposite — it promises a point of impact the weapon does not
   * have. The ring says "everything inside this is going to be hit", which is
   * the only question a shotgun ever asks.
   *
   * The radius is not decorative. It is the actual pellet cone projected
   * through the camera, so the circle you see is the circle the nine pellets go
   * into. Broken into arcs rather than drawn as a full circle because a solid
   * ring reads as a scope, and the gaps leave the target visible.
   */
  _drawPelletRing(ctx, state, cx, cy, col, lw, ads) {
    const s = this.s;
    // Half-angle of the cone -> pixels, via the same projection the camera uses.
    const fov = (state.fov ?? 80) * Math.PI / 180;
    const cone = state.pelletCone ?? 0.042;
    // Deliberately larger than life.
    //
    // The true cone is about two and a half degrees, which at an 80 degree
    // field of view is eighteen pixels — geometrically honest and useless to
    // look at: it read as a small rounded box rather than a ring, and you could
    // not tell what it was surrounding. Call of Duty oversizes this reticle for
    // the same reason. It still TRACKS the cone, so it tightens as the field of
    // view narrows on aiming; it is just legible while it does.
    const READABILITY = 2.2;
    let r = Math.tan(cone) * READABILITY / Math.tan(fov / 2) * (this.h / 2);
    r = clamp(r, 38 * s, Math.min(this.w, this.h) * 0.19);
    // Bloom opens it a little while firing, so it breathes with the weapon.
    r *= 1 + this.bloom * 0.10;

    // Four SHORT arcs. At 0.72 rad they nearly met and the whole thing read as
    // a closed octagon; a quarter of that leaves four distinct petals with the
    // target visible between them, which is the shape being copied.
    const SPAN = 0.58;
    const petals = [-Math.PI / 2, 0, Math.PI / 2, Math.PI];
    for (const pass of [['rgba(0,0,0,0.72)', lw * 1.2 + 3 * s], [col, lw * 1.2]]) {
      ctx.strokeStyle = pass[0];
      ctx.lineWidth = pass[1];
      ctx.lineCap = 'round';
      // A path PER arc. Four `arc` calls inside one path are joined end to end
      // by straight lines, which quietly filled in every gap and turned the
      // four petals into a closed octagon — it looked like a deliberate shape,
      // which is why it survived two rounds of tuning the angles.
      for (const a of petals) {
        ctx.beginPath();
        ctx.arc(cx, cy, r, a - SPAN / 2, a + SPAN / 2);
        ctx.stroke();
      }
    }
    void ads;
  }

  /**
   * Fallback target detection so the hostile reticle works before main.js feeds
   * it.
   *
   * Team-aware, which it was not: it returned true for ANY bot, so on a mode
   * where sides matter it would have raised the hostile cue over a teammate.
   * `main.js` always supplies `aimAtEnemy` so this never ran in a real match,
   * but a dead fallback that disagrees with the live path is a trap waiting for
   * whoever next changes the order these are set in.
   */
  enemyAimFallback(state) {
    const g = typeof window !== 'undefined' && window.__game?.game;
    if (!g || !g.enemies || !g.engine) return false;
    const cam = g.engine.camera;
    const dir = cam.getWorldDirection(_fv);
    const myTeam = g.player?.team;
    for (const e of g.enemies) {
      if (e.state === 'dead') continue;
      if (myTeam && e.team === myTeam) continue;
      _fp.copy(e.root.position); _fp.y += 1.1; _fp.sub(cam.position);
      const d = _fp.length();
      if (d < 0.5 || d > 90) continue;
      _fp.multiplyScalar(1 / d);
      // Angular radius of a ~0.55 m torso at that range, plus a little slop.
      if (_fp.dot(dir) > Math.cos(Math.atan(0.55 / d) + 0.004)) return true;
    }
    return false;
  }

  /* --------------------------------------------------------- hitmarker -- */

  _drawHitmarker(ctx, dt) {
    if (this.hitmarker <= 0) return;
    this.hitmarker -= dt;
    const cx = this.w / 2, cy = this.h / 2, s = this.s;
    const k = clamp(this.hitmarker / this.hitmarkerDur, 0, 1);
    const grow = easeOut(1 - k);
    const kind = this.hitKind;

    // Every state is the same diagonal X so it stays instantly readable as a
    // hit; weight, colour and the added element carry the hierarchy.
    const spec = {
      body:  { col: 'rgba(255,255,255,0.96)', lw: 2.6, r0: 7, r1: 15 },
      armor: { col: 'rgba(158,214,255,0.99)', lw: 2.8, r0: 7, r1: 15 },
      head:  { col: 'rgba(255,228,150,1)',    lw: 3.1, r0: 7, r1: 17 },
      kill:  { col: 'rgba(255,58,40,1)',      lw: 3.6, r0: 8, r1: 19 },
      multi: { col: 'rgba(255,170,36,1)',     lw: 4.0, r0: 8, r1: 21 },
    }[kind] || { col: '#fff', lw: 2.6, r0: 7, r1: 15 };

    const r0 = (spec.r0 + grow * 3) * s;
    const r1 = (spec.r1 + grow * 6) * s;
    const a = Math.min(1, k * 2.6);

    ctx.save();
    ctx.translate(cx, cy);
    ctx.globalAlpha = a;
    ctx.lineCap = 'butt';

    const strokePass = (fn) => {
      ctx.strokeStyle = 'rgba(0,0,0,0.55)'; ctx.lineWidth = spec.lw * s + 2.2 * s; fn();
      ctx.strokeStyle = spec.col; ctx.lineWidth = spec.lw * s; fn();
    };

    const cross = () => {
      ctx.beginPath();
      for (const [sx, sy] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
        ctx.moveTo(sx * r0, sy * r0); ctx.lineTo(sx * r1, sy * r1);
      }
      ctx.stroke();
    };
    strokePass(cross);

    if (kind === 'head') {
      // Headshot: a chevron seated clear above the X.
      ctx.globalAlpha = a;
      strokePass(() => {
        ctx.beginPath();
        ctx.moveTo(-r1 * 0.62, -r1 * 1.28);
        ctx.lineTo(0, -r1 * 1.82);
        ctx.lineTo(r1 * 0.62, -r1 * 1.28);
        ctx.stroke();
      });
    } else if (kind === 'armor') {
      // Armour: a plate outline sitting outside the X.
      ctx.globalAlpha = a * 0.85;
      ctx.strokeStyle = 'rgba(0,0,0,0.5)'; ctx.lineWidth = 3.4 * s;
      const hex = () => {
        ctx.beginPath();
        const R = r1 * 1.62;
        for (let i = 0; i < 6; i++) {
          const t = (i / 6) * 6.2832 + 0.5236;
          const px = Math.cos(t) * R, py = Math.sin(t) * R * 0.86;
          i ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
        }
        ctx.closePath(); ctx.stroke();
      };
      hex();
      ctx.strokeStyle = spec.col; ctx.lineWidth = 1.8 * s;
      hex();
    } else if (kind === 'kill' || kind === 'multi') {
      // Expanding shockwave ring locked to the kill sting.
      const rr = r1 * (1.15 + grow * 1.7);
      ctx.globalAlpha = a * (1 - grow) * 0.9;
      ctx.strokeStyle = spec.col; ctx.lineWidth = (kind === 'multi' ? 3 : 2) * s;
      ctx.beginPath(); ctx.arc(0, 0, rr, 0, 6.2832); ctx.stroke();
      if (kind === 'multi') {
        ctx.globalAlpha = a * (1 - grow) * 0.45;
        ctx.beginPath(); ctx.arc(0, 0, rr * 1.34, 0, 6.2832); ctx.stroke();
        ctx.globalAlpha = a;
        vtext(ctx, 'X' + this.hitCombo, r1 * 1.5, -r1 * 1.15, 17 * s, { color: spec.col, weight: 0.17, track: 2 * s });
      }
    }
    ctx.restore();
  }

  /* ------------------------------------------------------- damage ring -- */

  _drawDamageRing(ctx, dt) {
    if (!this.hitDirs.length) return;
    const cx = this.w / 2, cy = this.h / 2, s = this.s;
    for (let i = this.hitDirs.length - 1; i >= 0; i--) {
      const d = this.hitDirs[i];
      d.life -= dt;
      d.pop = Math.max(0, (d.pop || 0) - dt * 5);
      if (d.life <= 0) { this.hitDirs.splice(i, 1); continue; }

      const k = clamp(d.life / 1.25, 0, 1);
      const a = easeOut(k) * (0.62 + 0.12 * (d.stack || 1));
      const R = (124 + (d.stack || 1) * 4) * s * (1 + d.pop * 0.05);
      const span = (0.32 + (d.stack || 1) * 0.05);
      const thick = (20 + (d.stack || 1) * 5) * s * (1 + d.pop * 0.3);

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(d.angle);
      ctx.globalAlpha = a;

      // Arc segment: a wedge on the ring, bright at the centre of the span,
      // feathered at both ends so stacked hits read as one blooming sector.
      const g = ctx.createLinearGradient(-R * span, 0, R * span, 0);
      g.addColorStop(0.0, 'rgba(255,40,24,0)');
      g.addColorStop(0.5, 'rgba(255,72,48,1)');
      g.addColorStop(1.0, 'rgba(255,40,24,0)');
      ctx.strokeStyle = g;
      ctx.lineWidth = thick;
      ctx.lineCap = 'butt';
      ctx.beginPath();
      ctx.arc(0, 0, R, -Math.PI / 2 - span, -Math.PI / 2 + span);
      ctx.stroke();

      // Hot inner edge: keeps the arc crisp against a blown-out sky and gives
      // the wedge a direction (bright toward the player).
      const g2 = ctx.createLinearGradient(-R * span * 0.8, 0, R * span * 0.8, 0);
      g2.addColorStop(0, 'rgba(255,214,200,0)');
      g2.addColorStop(0.5, 'rgba(255,222,208,0.85)');
      g2.addColorStop(1, 'rgba(255,214,200,0)');
      ctx.globalAlpha = a * 0.55;
      ctx.strokeStyle = g2;
      ctx.lineWidth = 2.4 * s;
      ctx.beginPath();
      ctx.arc(0, 0, R - thick * 0.42, -Math.PI / 2 - span * 0.72, -Math.PI / 2 + span * 0.72);
      ctx.stroke();
      ctx.restore();
    }
  }

  /* ------------------------------------------------------------- ammo --- */

  _drawAmmo(ctx, state, mag, B) {
    const { w, h, s } = this;
    const M = 54 * s;
    const slideIn = (1 - B) * 70 * s;
    const x = w - M + slideIn;
    const baseY = h - M - 46 * s;      // leaves room for the mag strip below

    // Melee: no magazine, no reserve, no round strip. Showing "00 / 0" in the
    // low-ammo red for a knife reads as a broken weapon.
    if (state.melee) {
      ctx.save();
      ctx.globalAlpha = B;
      vtext(ctx, (state.weaponName ?? '').toUpperCase(), x, baseY + 34 * s, 15 * s,
        { color: CO.hi, weight: 0.15, track: 5 * s, align: 'right' });
      vtext(ctx, 'MELEE', x, baseY + 56 * s, 10 * s,
        { color: CO.faint, weight: 0.16, track: 4 * s, align: 'right' });
      ctx.restore();
      return;
    }

    const magSize = state.magSize ?? 30;
    const reserve = state.reserve ?? 0;
    const low = mag <= magSize * 0.25;
    this.lowAmmoPulse = low ? this.lowAmmoPulse + 0.05 : 0;
    const pulse = low ? 0.65 + Math.sin(this.lowAmmoPulse * 3.2) * 0.35 : 1;

    ctx.save();
    ctx.globalAlpha = B;

    /* reserve sits flush right; the magazine reads large to its left,
       exactly the weight relationship CoD uses. */
    const big = 66 * s;
    const flash = this.ammoFlash;
    const resStr = String(reserve);
    const resSize = 24 * s;
    const resW = vwidth(resStr, resSize, resSize * 0.14);
    vtext(ctx, resStr, x, baseY - 3 * s, resSize, {
      color: 'rgba(176,192,208,0.92)', weight: 0.13, align: 'right',
    });

    const sepX = x - resW - 13 * s;
    ctx.strokeStyle = 'rgba(160,178,196,0.5)';
    ctx.lineWidth = 1.6 * s;
    ctx.beginPath();
    ctx.moveTo(sepX + 7 * s, baseY - big * 0.86);
    ctx.lineTo(sepX - 7 * s, baseY + 3 * s);
    ctx.stroke();

    const col = low
      ? `rgba(255,${(70 + 60 * pulse) | 0},${(48 * pulse) | 0},${0.72 + 0.28 * pulse})`
      : `rgba(${236 + flash * 19},${244 + flash * 11},${252},0.99)`;
    const magStr = String(mag).padStart(2, '0');
    const magX = sepX - 13 * s;
    ctx.save();
    ctx.globalAlpha = B * (1 - Math.abs(this.ammoRoll) * 0.2);
    vtext(ctx, magStr, magX, baseY + this.ammoRoll * 5 * s, big, {
      color: col, weight: 0.145 + flash * 0.02, track: big * 0.09, align: 'right',
    });
    ctx.restore();

    // muzzle-flash bloom on the counter when a round leaves the gun
    if (flash > 0.01) {
      ctx.save();
      ctx.globalAlpha = flash * 0.55 * B;
      ctx.filter = 'blur(7px)';
      vtext(ctx, magStr, magX, baseY, big, { color: '#fff', weight: 0.17, track: big * 0.09, align: 'right', contour: false });
      ctx.restore();
    }

    /* Round strip — segments while loaded, a progress track while reloading.
       Same slot, so the block never changes height. */
    const reloading = !!state.reloading;
    const stripY = baseY + 14 * s;
    const segN = Math.min(magSize, 30);
    const segW = 5.6 * s, segGap = 2.6 * s;
    const stripW = segN * segW + (segN - 1) * segGap;
    if (!reloading) {
      const filled = Math.round((mag / magSize) * segN);
      for (let i = 0; i < segN; i++) {
        // grouped in fives so the strip reads as a magazine, not a dashed rule
        const sx = x - stripW - ((segN - 1) / 5 | 0) * segGap * 1.6
          + i * (segW + segGap) + ((i / 5) | 0) * segGap * 1.6;
        const on = i < filled;
        ctx.fillStyle = 'rgba(0,0,0,0.4)';
        ctx.fillRect(sx - 0.8 * s, stripY - 0.8 * s, segW + 1.6 * s, 8.6 * s);
        ctx.fillStyle = on
          ? (low ? `rgba(255,96,64,${0.55 + 0.45 * pulse})` : 'rgba(230,242,254,0.95)')
          : 'rgba(255,255,255,0.16)';
        ctx.fillRect(sx, stripY + (on ? 0 : 2 * s), segW, on ? 7 * s : 3 * s);
      }
    }

    /* weapon name + fire mode */
    const nameY = baseY - big - 14 * s;
    const name = String(state.weaponName ?? '').toUpperCase();
    vtext(ctx, name, x, nameY, 17 * s, { color: CO.hi, weight: 0.13, track: 4.2 * s, align: 'right' });
    const nw = vwidth(name, 17 * s, 4.2 * s);
    const mode = (state.fireMode ?? (magSize > 20 ? 'AUTO' : 'SEMI')).toUpperCase();
    const mw = vwidth(mode, 10 * s, 2.6 * s) + 14 * s;
    ctx.strokeStyle = 'rgba(255,196,74,0.55)';
    ctx.lineWidth = 1 * s;
    chamfer(ctx, x - nw - mw - 10 * s, nameY - 15 * s, mw, 19 * s, 4 * s, 0b0101);
    ctx.fillStyle = 'rgba(255,196,74,0.10)'; ctx.fill();
    ctx.stroke();
    vtext(ctx, mode, x - nw - mw - 3 * s, nameY - 2 * s, 10 * s, {
      color: CO.accent, weight: 0.15, track: 2.6 * s, align: 'left',
    });

    /* reload progress, occupying the round-strip slot */
    if (reloading && !this.wasReloading) this.reloadT = 0;
    this.wasReloading = reloading;
    if (reloading) {
      this.reloadT += 0.016;
      const dur = state.reloadTime ?? 2.1;
      const p = clamp(this.reloadT / dur, 0, 1);
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(x - stripW, stripY, stripW, 7 * s);
      ctx.fillStyle = CO.accent;
      ctx.fillRect(x - stripW, stripY, stripW * p, 7 * s);
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.fillRect(x - stripW + stripW * p - 1.6 * s, stripY, 1.6 * s, 7 * s);
      const blink = 0.6 + 0.4 * Math.sin(this.time * 9);
      vtext(ctx, 'RELOADING', x, stripY + 20 * s, 12 * s, {
        color: `rgba(255,196,74,${blink})`, weight: 0.15, track: 3.2 * s, align: 'right',
      });
    }
    ctx.restore();
  }

  /* ----------------------------------------------------------- vitals --- */

  _drawVitals(ctx, state, hp, B, dt) {
    const { h, s } = this;
    const M = 54 * s;
    const x = M - (1 - B) * 70 * s;
    const bw = 268 * s, bh = 10 * s;
    const y = h - M - 96 * s;

    ctx.save();
    ctx.globalAlpha = B;

    // track
    ctx.fillStyle = 'rgba(4,8,12,0.5)';
    chamfer(ctx, x - 2 * s, y - 2 * s, bw + 4 * s, bh + 4 * s, 4 * s, 0b0101); ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.08)';
    ctx.fillRect(x, y, bw, bh);

    // chip-damage ghost
    if (this.hpGhost > this.hpNow + 0.001) {
      ctx.fillStyle = 'rgba(255,58,44,0.55)';
      ctx.fillRect(x + bw * this.hpNow, y, bw * (this.hpGhost - this.hpNow), bh);
    }

    // live value
    const g = ctx.createLinearGradient(x, 0, x + bw, 0);
    if (hp > 0.55) { g.addColorStop(0, 'rgba(96,206,126,1)'); g.addColorStop(1, 'rgba(168,226,120,1)'); }
    else if (hp > 0.3) { g.addColorStop(0, 'rgba(255,168,32,1)'); g.addColorStop(1, 'rgba(255,206,96,1)'); }
    else { g.addColorStop(0, 'rgba(255,48,36,1)'); g.addColorStop(1, 'rgba(255,110,84,1)'); }
    ctx.fillStyle = g;
    ctx.fillRect(x, y, bw * this.hpNow, bh);

    // leading edge highlight
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    ctx.fillRect(x + bw * this.hpNow - 1.6 * s, y, 1.6 * s, bh);

    // segment notches every 25 units
    ctx.strokeStyle = 'rgba(0,0,0,0.45)'; ctx.lineWidth = 1 * s;
    ctx.beginPath();
    for (let i = 1; i < 4; i++) { ctx.moveTo(x + bw * i / 4, y); ctx.lineTo(x + bw * i / 4, y + bh); }
    ctx.stroke();

    // regen sweep
    if (this.regenT > 1.4 && hp < 0.999) {
      const sw = ((this.time * 0.9) % 1);
      const gg = ctx.createLinearGradient(x + bw * sw - 40 * s, 0, x + bw * sw + 40 * s, 0);
      gg.addColorStop(0, 'rgba(255,255,255,0)');
      gg.addColorStop(0.5, 'rgba(255,255,255,0.30)');
      gg.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.save(); ctx.beginPath(); ctx.rect(x, y, bw * this.hpNow, bh); ctx.clip();
      ctx.fillStyle = gg; ctx.fillRect(x, y, bw, bh); ctx.restore();
    }

    // label + numeric readout
    vtext(ctx, 'VITALS', x, y - 10 * s, 11 * s, { color: CO.faint, weight: 0.15, track: 3.6 * s });
    // The ACTUAL health, not a percentage of it. Offline the maximum is 100 and
    // the two are identical; online players have three times that, and a bar
    // that always says 100 hides the whole difference.
    const hpTxt = String(Math.max(0, Math.round(state.health ?? hp * 100)));
    const crit = hp < 0.34;
    vtext(ctx, hpTxt, x + bw, y - 9 * s, 18 * s, {
      color: crit ? `rgba(255,${(70 + 70 * (0.5 + 0.5 * Math.sin(this.lowPulse * 4.2))) | 0},60,1)` : CO.hi,
      weight: 0.14, align: 'right',
    });
    const hw = vwidth(hpTxt, 18 * s, 18 * s * 0.16);
    vtext(ctx, 'HP', x + bw - hw - 7 * s, y - 9 * s, 10 * s, { color: CO.faint, weight: 0.16, track: 2.6 * s, align: 'right' });
    ctx.restore();
  }

  /* -------------------------------------------------------- equipment --- */

  _drawEquipment(ctx, state, B) {
    const { h, s } = this;
    const M = 54 * s;
    const y = h - M + 2 * s;
    const slots = state.equipment ?? [
      { key: 'G', kind: 'frag', n: 2 },
      { key: 'Q', kind: 'flash', n: 2 },
      { key: '4', kind: 'field', n: 1 },
    ];
    ctx.save();
    for (let i = 0; i < slots.length; i++) {
      const st = slots[i];
      const t = clamp((this.boot - 0.18 - i * 0.07) / 0.4, 0, 1);
      if (t <= 0) continue;
      const e = easeBack(t);
      const bx = M + i * 56 * s - (1 - e) * 24 * s;
      const size = 44 * s;
      ctx.globalAlpha = t;
      ctx.save();
      ctx.translate(bx, y - size);
      chamfer(ctx, 0, 0, size, size, 7 * s, 0b0101);
      ctx.fillStyle = st.n > 0 ? 'rgba(6,11,16,0.46)' : 'rgba(6,11,16,0.26)';
      ctx.fill();
      ctx.strokeStyle = st.n > 0 ? 'rgba(190,208,224,0.35)' : 'rgba(190,208,224,0.14)';
      ctx.lineWidth = 1 * s; ctx.stroke();

      const ic = st.n > 0 ? 'rgba(226,240,252,0.95)' : 'rgba(226,240,252,0.28)';
      ctx.strokeStyle = ic; ctx.fillStyle = ic; ctx.lineWidth = 1.7 * s;
      const c = size / 2;
      if (st.kind === 'frag') {
        ctx.beginPath(); ctx.ellipse(c, c + 2 * s, 7 * s, 8.5 * s, 0, 0, 6.2832); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(c - 3 * s, c - 7 * s); ctx.lineTo(c + 3 * s, c - 7 * s);
        ctx.lineTo(c + 3 * s, c - 5 * s); ctx.lineTo(c - 3 * s, c - 5 * s); ctx.closePath(); ctx.fill();
        ctx.beginPath(); ctx.arc(c + 6 * s, c - 8 * s, 3 * s, 0, 6.2832); ctx.stroke();
      } else if (st.kind === 'flash') {
        ctx.beginPath();
        ctx.moveTo(c - 5 * s, c - 8 * s); ctx.lineTo(c + 5 * s, c - 8 * s);
        ctx.lineTo(c + 5 * s, c + 8 * s); ctx.lineTo(c - 5 * s, c + 8 * s); ctx.closePath(); ctx.stroke();
        ctx.beginPath();
        for (let r = 0; r < 4; r++) {
          const a = -0.9 + r * 0.6;
          ctx.moveTo(c + Math.cos(a) * 8 * s, c + Math.sin(a) * 8 * s);
          ctx.lineTo(c + Math.cos(a) * 12 * s, c + Math.sin(a) * 12 * s);
        }
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.moveTo(c, c - 9 * s); ctx.lineTo(c + 8 * s, c - 5 * s); ctx.lineTo(c + 8 * s, c + 3 * s);
        ctx.lineTo(c, c + 9 * s); ctx.lineTo(c - 8 * s, c + 3 * s); ctx.lineTo(c - 8 * s, c - 5 * s);
        ctx.closePath(); ctx.stroke();
      }
      ctx.restore();

      // key hint chip clipped to the tile's top-left corner
      ctx.fillStyle = 'rgba(255,255,255,0.14)';
      ctx.fillRect(bx, y - size, 14 * s, 13 * s);
      vtext(ctx, st.key, bx + 7 * s, y - size + 11 * s, 9 * s, { color: CO.hi, weight: 0.17, align: 'center' });
      if (st.n > 0) {
        vtext(ctx, String(st.n), bx + size - 4 * s, y - 5 * s, 13 * s,
          { color: st.n > 0 ? CO.hi : CO.faint, weight: 0.15, align: 'right' });
      }
    }
    ctx.restore();
  }

  /* ---------------------------------------------------------- minimap --- */

  _drawMinimap(ctx, state, B) {
    const { s } = this;
    const M = 54 * s;
    const size = 196 * s;
    const x = M - (1 - B) * 60 * s, y = M;
    const cx = x + size / 2, cy = y + size / 2;
    const yaw = state.yaw ?? 0;
    const scale = size / 56;                 // ~56 m across the frame

    ctx.save();
    ctx.globalAlpha = B;

    // frame
    chamfer(ctx, x, y, size, size, 14 * s, 0b1111);
    ctx.fillStyle = 'rgba(7,11,16,0.90)';
    ctx.fill();
    ctx.save();
    ctx.clip();

    // subtle grid so the plate reads as a display even with no geometry
    ctx.strokeStyle = 'rgba(140,170,200,0.06)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 1; i < 6; i++) {
      ctx.moveTo(x + (size * i) / 6, y); ctx.lineTo(x + (size * i) / 6, y + size);
      ctx.moveTo(x, y + (size * i) / 6); ctx.lineTo(x + size, y + (size * i) / 6);
    }
    ctx.stroke();

    const pos = this._playerPos(state);
    const rects = this._mapData(state);

    if (pos && rects) {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.scale(scale, scale);
      ctx.rotate(yaw);
      ctx.translate(-pos.x, -pos.z);
      // Two-tier read: buildings are solid and bright, low cover is a dim
      // wash, so the traversable street reads as negative space.
      for (const pass of [false, true]) {
        ctx.fillStyle = pass ? 'rgba(196,214,232,0.42)' : 'rgba(120,142,164,0.20)';
        for (const r of rects) {
          if (r.tall !== pass) continue;
          if (Math.abs(r.x - pos.x) > 60 || Math.abs(r.z - pos.z) > 60) continue;
          ctx.fillRect(r.x, r.z, r.w, r.d);
        }
      }
      ctx.lineWidth = 0.16;
      ctx.strokeStyle = 'rgba(226,240,252,0.5)';
      ctx.beginPath();
      for (const r of rects) {
        if (!r.tall) continue;
        if (Math.abs(r.x - pos.x) > 60 || Math.abs(r.z - pos.z) > 60) continue;
        ctx.rect(r.x, r.z, r.w, r.d);
      }
      ctx.stroke();
      ctx.restore();
    }

    // view cone
    ctx.save();
    ctx.translate(cx, cy);
    const cone = ctx.createRadialGradient(0, 0, 0, 0, 0, size * 0.46);
    cone.addColorStop(0, 'rgba(150,210,255,0.24)');
    cone.addColorStop(1, 'rgba(150,210,255,0)');
    ctx.fillStyle = cone;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, size * 0.46, -Math.PI / 2 - 0.62, -Math.PI / 2 + 0.62);
    ctx.closePath(); ctx.fill();
    ctx.restore();

    /*
     * Bomb sites. Drawn under the blips and clamped to the plate edge, because
     * a site you cannot see on the minimap is a site you cannot navigate to —
     * and both sites are usually outside a 56 m window.
     */
    if (pos && state.sites) {
      const r = size / 2 - 12 * s;
      for (const site of state.sites) {
        const dx = site.x - pos.x, dz = site.z - pos.z;
        let sx = (dx * Math.cos(yaw) - dz * Math.sin(yaw)) * scale;
        let sy = (dx * Math.sin(yaw) + dz * Math.cos(yaw)) * scale;
        const d = Math.hypot(sx, sy);
        const edge = d > r;
        if (edge) { sx = (sx / d) * r; sy = (sy / d) * r; }

        ctx.save();
        ctx.translate(cx + sx, cy + sy);
        const col = state.bombSiteLive === site.label
          ? 'rgba(255,110,92,0.95)' : 'rgba(255,196,96,0.85)';
        ctx.globalAlpha = B * (edge ? 0.7 : 1);
        // Diamond plate so the letter reads against map geometry.
        ctx.beginPath();
        const q = 9 * s;
        ctx.moveTo(0, -q); ctx.lineTo(q, 0); ctx.lineTo(0, q); ctx.lineTo(-q, 0);
        ctx.closePath();
        ctx.fillStyle = 'rgba(7,11,16,0.8)';
        ctx.fill();
        ctx.strokeStyle = col;
        ctx.lineWidth = 1.4 * s;
        ctx.stroke();
        vtext(ctx, site.label, 0, 4 * s, 11 * s,
          { color: col, weight: 0.18, track: 0, align: 'center' });
        ctx.restore();
      }
    }

    // enemy blips
    const blips = this._blips(state);
    if (pos && blips) {
      for (const b of blips) {
        if (b.dead) continue;
        // Undetected enemies are not drawn: the minimap is intelligence, not
        // omniscience. A UAV sets `known` on everyone.
        if (b.known === false) continue;
        const dx = b.x - pos.x, dz = b.z - pos.z;
        const bx = (dx * Math.cos(yaw) - dz * Math.sin(yaw)) * scale;
        const by = (dx * Math.sin(yaw) + dz * Math.cos(yaw)) * scale;
        const r = Math.hypot(bx, by);
        const lim = size * 0.46;
        const edge = r > lim;
        const k = edge ? lim / r : 1;
        const px = cx + bx * k, py = cy + by * k;
        const hot = b.hot !== false;
        const pulse = 0.55 + 0.45 * Math.sin(this.time * 6 + (b.x + b.z));
        ctx.save();
        ctx.translate(px, py);
        if (edge) {
          // off-map contact: chevron pinned to the rim
          ctx.rotate(Math.atan2(by, bx) + Math.PI / 2);
          ctx.fillStyle = `rgba(255,64,44,${0.5 + 0.35 * pulse})`;
          ctx.beginPath();
          ctx.moveTo(0, -5 * s); ctx.lineTo(4 * s, 2 * s); ctx.lineTo(-4 * s, 2 * s);
          ctx.closePath(); ctx.fill();
        } else {
          ctx.rotate(Math.PI / 4);
          const q = (hot ? 5.4 : 4) * s;
          ctx.fillStyle = hot ? `rgba(255,64,44,${0.65 + 0.35 * pulse})` : 'rgba(255,110,90,0.45)';
          ctx.strokeStyle = 'rgba(0,0,0,0.5)'; ctx.lineWidth = 1 * s;
          ctx.fillRect(-q / 2, -q / 2, q, q);
          ctx.strokeRect(-q / 2, -q / 2, q, q);
        }
        ctx.restore();
      }
    }

    // player arrow
    ctx.save();
    ctx.translate(cx, cy);
    ctx.fillStyle = 'rgba(240,250,255,0.98)';
    ctx.strokeStyle = 'rgba(0,0,0,0.6)'; ctx.lineWidth = 1.2 * s;
    ctx.beginPath();
    ctx.moveTo(0, -7 * s); ctx.lineTo(5.2 * s, 6 * s); ctx.lineTo(0, 3 * s); ctx.lineTo(-5.2 * s, 6 * s);
    ctx.closePath(); ctx.fill(); ctx.stroke();
    ctx.restore();
    ctx.restore();   // unclip

    // frame line + brackets + north pip
    chamfer(ctx, x, y, size, size, 14 * s, 0b1111);
    ctx.strokeStyle = 'rgba(196,214,232,0.26)';
    ctx.lineWidth = 1.2 * s;
    ctx.stroke();
    bracket(ctx, x, y, size, size, 16 * s, 'rgba(226,240,252,0.6)', 2 * s);

    const nr = size / 2 - 9 * s;
    const na = -Math.PI / 2 + yaw;
    ctx.fillStyle = CO.accent;
    ctx.beginPath();
    ctx.arc(cx + Math.cos(na) * nr, cy + Math.sin(na) * nr, 2.6 * s, 0, 6.2832);
    ctx.fill();

    // header strip
    vtext(ctx, state.mapName ?? 'AL-RASHEED', x + 3 * s, y - 10 * s, 10.5 * s, { color: 'rgba(186,202,218,0.8)', weight: 0.16, track: 3.2 * s });
    // Deathmatch has a target now, so the tally says what it is counting
    // towards. Without the limit "07" is a number with no shape to it.
    const scoreTxt = state.dmLimit
      ? `${String(this.kills).padStart(2, '0')}/${state.dmLimit}`
      : String(this.kills).padStart(2, '0');
    const sw2 = vwidth(scoreTxt, 15 * s, 2 * s);
    vtext(ctx, scoreTxt, x + size, y - 10 * s, 15 * s, { color: CO.hi, weight: 0.15, track: 2 * s, align: 'right' });
    vtext(ctx, 'ELIMS', x + size - sw2 - 7 * s, y - 10 * s, 9 * s, { color: CO.faint, weight: 0.17, track: 2.4 * s, align: 'right' });
    // The match clock, under the map name. Small: it matters at the end of a
    // match and nowhere else, so it must not compete with the ammo or the health.
    if (state.dmClock != null) {
      const m = Math.floor(state.dmClock / 60);
      const sec = Math.floor(state.dmClock % 60);
      vtext(ctx, `${m}:${String(sec).padStart(2, '0')}`, x + 3 * s, y + size + 13 * s, 10 * s,
        { color: state.dmClock < 60 ? CO.warn : CO.faint, weight: 0.16, track: 2.6 * s });
    }
    ctx.restore();
  }

  /* ---------------------------------------------------------- compass --- */

  _drawCompass(ctx, state, B) {
    const { w, s } = this;
    const cx = w / 2;
    const y = 66 * s - (1 - B) * 26 * s;
    const halfW = 250 * s;
    const yaw = state.yaw ?? 0;
    const deg = ((-yaw * 180 / Math.PI) % 360 + 360) % 360;

    ctx.save();
    ctx.globalAlpha = B;

    // Feathered backing instead of a hard box — keeps the sky readable.
    const g = ctx.createLinearGradient(cx - halfW, 0, cx + halfW, 0);
    g.addColorStop(0, 'rgba(4,8,12,0)');
    g.addColorStop(0.5, 'rgba(4,8,12,0.24)');
    g.addColorStop(1, 'rgba(4,8,12,0)');
    ctx.fillStyle = g;
    ctx.fillRect(cx - halfW, y - 18 * s, halfW * 2, 33 * s);
    // hairline rule the ticks hang from
    const g2 = ctx.createLinearGradient(cx - halfW, 0, cx + halfW, 0);
    g2.addColorStop(0, 'rgba(226,240,252,0)');
    g2.addColorStop(0.5, 'rgba(226,240,252,0.32)');
    g2.addColorStop(1, 'rgba(226,240,252,0)');
    ctx.fillStyle = g2;
    ctx.fillRect(cx - halfW, y + 2 * s, halfW * 2, 1 * s);

    ctx.save();
    ctx.beginPath();
    ctx.rect(cx - halfW, y - 24 * s, halfW * 2, 48 * s);
    ctx.clip();

    const pxPerDeg = halfW / 62;
    const CARD = { 0: 'N', 45: 'NE', 90: 'E', 135: 'SE', 180: 'S', 225: 'SW', 270: 'W', 315: 'NW' };
    for (let a = -70; a <= 70; a += 5) {
      const abs = ((Math.round(deg / 5) * 5 + a) % 360 + 360) % 360;
      const off = abs - deg;
      const d = ((off + 540) % 360) - 180;
      const x = cx + d * pxPerDeg;
      const fade = clamp(1 - Math.abs(d) / 66, 0, 1);
      const card = CARD[abs];
      const major = abs % 45 === 0;
      ctx.globalAlpha = B * fade;
      ctx.strokeStyle = major ? 'rgba(240,248,255,0.9)' : 'rgba(214,228,242,0.5)';
      ctx.lineWidth = major ? 1.8 * s : 1.1 * s;
      ctx.beginPath();
      ctx.moveTo(x, y + 3 * s);
      ctx.lineTo(x, y + (major ? 14 * s : 9 * s));
      ctx.stroke();
      if (card) {
        vtext(ctx, card, x, y - 2 * s, card.length > 1 ? 11 * s : 15 * s, {
          color: card === 'N' ? CO.accent : CO.hi,
          weight: card.length > 1 ? 0.16 : 0.14, track: 1.8 * s, align: 'center',
        });
      } else if (abs % 30 === 0) {
        vtext(ctx, String(abs).padStart(2, '0'), x, y - 3 * s, 9.5 * s,
          { color: 'rgba(196,212,228,0.72)', weight: 0.17, track: 1.6 * s, align: 'center' });
      }
    }

    // enemy bearing chevrons riding the ruler
    const pos = this._playerPos(state);
    const blips = this._blips(state);
    if (pos && blips) {
      for (const b of blips) {
        if (b.dead || b.hot === false) continue;
        const world = Math.atan2(b.x - pos.x, b.z - pos.z);
        let rel = ((-world + yaw + Math.PI) % (Math.PI * 2));
        rel = ((rel + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
        const d = rel * 180 / Math.PI;
        if (Math.abs(d) > 68) continue;
        const x = cx + d * pxPerDeg;
        ctx.globalAlpha = B * clamp(1 - Math.abs(d) / 70, 0, 1) * 0.95;
        ctx.fillStyle = CO.enemy;
        ctx.beginPath();
        ctx.moveTo(x, y + 17 * s); ctx.lineTo(x + 4.5 * s, y + 24 * s); ctx.lineTo(x - 4.5 * s, y + 24 * s);
        ctx.closePath(); ctx.fill();
      }
    }
    ctx.restore();

    // heading readout in a chamfered chip
    ctx.globalAlpha = B;
    const hd = String(Math.round(deg)).padStart(3, '0');
    const cw = 54 * s;
    chamfer(ctx, cx - cw / 2, y - 50 * s, cw, 20 * s, 5 * s, 0b1111);
    ctx.fillStyle = 'rgba(4,8,12,0.66)'; ctx.fill();
    ctx.strokeStyle = 'rgba(200,220,240,0.34)'; ctx.lineWidth = 1 * s; ctx.stroke();
    vtext(ctx, hd, cx, y - 36 * s, 12 * s, { color: CO.hi, weight: 0.14, track: 2 * s, align: 'center' });

    // centre index, tethered to the heading chip
    ctx.strokeStyle = 'rgba(226,240,252,0.35)'; ctx.lineWidth = 1 * s;
    ctx.beginPath(); ctx.moveTo(cx, y - 30 * s); ctx.lineTo(cx, y - 27 * s); ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.beginPath();
    ctx.moveTo(cx, y - 19 * s); ctx.lineTo(cx - 5 * s, y - 27 * s); ctx.lineTo(cx + 5 * s, y - 27 * s);
    ctx.closePath(); ctx.fill();
    ctx.restore();
  }

  /* --------------------------------------------------------- killfeed --- */

  _drawKillfeed(ctx, dt, B) {
    const { w, s } = this;
    const M = 54 * s;
    ctx.save();
    for (let i = this.killfeed.length - 1; i >= 0; i--) {
      const k = this.killfeed[i];
      k.life -= dt;
      k.age += dt;
      if (k.slot > i) k.slot = Math.max(i, k.slot - dt * 8);
      if (k.life <= 0) { this.killfeed.splice(i, 1); continue; }

      const inT = easeOut(clamp(k.age / 0.28, 0, 1));
      const out = clamp(k.life / 0.5, 0, 1);
      const a = inT * out;
      const rowH = 27 * s;
      const y = M + 12 * s + k.slot * rowH;
      const slide = (1 - inT) * 40 * s;

      const fs = 12.5 * s, tr = 2.2 * s;
      const wA = vwidth(k.actor, fs, tr);
      const wV = vwidth(k.victim, fs, tr);
      const icon = 26 * s;
      const pad = 10 * s;
      const total = wA + wV + icon + pad * 3;
      const x = w - M + slide;

      ctx.globalAlpha = a;
      chamfer(ctx, x - total, y - 17 * s, total, 22 * s, 5 * s, 0b0101);
      ctx.fillStyle = 'rgba(4,8,12,0.5)'; ctx.fill();
      // Accent bar: red on a team kill, so it reads as a mistake at a glance.
      ctx.fillStyle = k.friendly ? 'rgba(255,96,84,0.95)' : 'rgba(112,206,255,0.9)';
      ctx.fillRect(x - total, y - 17 * s, 2.2 * s, 22 * s);

      let tx = x - total + pad;
      vtext(ctx, k.actor, tx, y - 3 * s, fs, { color: CO.friend, weight: 0.14, track: tr });
      tx += wA + pad * 0.9;
      this._weaponGlyph(ctx, tx, y - 9 * s, icon, 'rgba(226,240,252,0.9)');
      tx += icon + pad * 0.9;
      vtext(ctx, k.victim, tx, y - 3 * s, fs,
        { color: k.friendly ? CO.friend : CO.enemy, weight: 0.14, track: tr });
    }
    ctx.restore();
  }

  /** Tiny generic rifle silhouette for the killfeed. */
  _weaponGlyph(ctx, x, y, size, col) {
    const u = size / 26;
    ctx.save();
    ctx.translate(x, y);
    ctx.strokeStyle = 'rgba(0,0,0,0.5)'; ctx.lineWidth = 3 * u; ctx.lineJoin = 'round';
    const path = () => {
      ctx.beginPath();
      ctx.moveTo(1 * u, 2 * u); ctx.lineTo(24 * u, 2 * u);          // barrel
      ctx.moveTo(4 * u, 2 * u); ctx.lineTo(4 * u, 6 * u);           // mag
      ctx.moveTo(9 * u, 2 * u); ctx.lineTo(11 * u, 7 * u);          // grip
      ctx.moveTo(14 * u, 0.5 * u); ctx.lineTo(19 * u, 0.5 * u);     // rail
    };
    path(); ctx.stroke();
    ctx.strokeStyle = col; ctx.lineWidth = 1.6 * u;
    path(); ctx.stroke();
    ctx.restore();
  }

  /* --------------------------------------------------------- streaks ---- */

  _drawStreaks(ctx, dt, B, state) {
    const { s } = this;
    const M = 54 * s;
    const y = M + 196 * s + 26 * s;
    ctx.save();
    ctx.globalAlpha = B;
    vtext(ctx, 'KILLSTREAKS', M, y, 10 * s, { color: CO.faint, weight: 0.15, track: 3.2 * s });
    for (let i = 0; i < STREAKS.length; i++) {
      const st = STREAKS[i];
      // Earned-and-unspent, not "have you ever reached this count" — spending a
      // streak has to visibly take it off the rail.
      const ready = state?.streakEarned
        ? state.streakEarned.includes(st.id)
        : this.kills >= st.at;
      const bx = M + i * 46 * s, by = y + 8 * s, sz = 36 * s;
      const t = clamp((this.boot - 0.3 - i * 0.06) / 0.4, 0, 1);
      ctx.globalAlpha = B * t;
      chamfer(ctx, bx, by, sz, sz, 6 * s, 0b0101);
      ctx.fillStyle = ready ? 'rgba(255,196,74,0.16)' : 'rgba(6,11,16,0.4)';
      ctx.fill();
      ctx.strokeStyle = ready ? 'rgba(255,196,74,0.7)' : 'rgba(190,208,224,0.16)';
      ctx.lineWidth = 1 * s; ctx.stroke();
      const col = ready ? CO.accent : 'rgba(200,214,228,0.3)';
      this._streakIcon(ctx, st.icon, bx + sz / 2, by + sz / 2 - 2 * s, sz * 0.40, col);
      if (!ready) {
        vtext(ctx, String(Math.max(0, st.at - this.kills)), bx + sz / 2, by + sz - 4 * s, 10 * s,
          { color: 'rgba(214,226,238,0.75)', weight: 0.17, align: 'center' });
      }
    }

    // Which key spends it. Without this the rail tells you something is ready
    // and nothing tells you how to use it — which is how it read as broken.
    if (state?.streakReady) {
      const hy = y + 8 * s + 36 * s + 14 * s;
      ctx.globalAlpha = B * (0.7 + 0.3 * Math.sin(this.time * 3.2));
      vtext(ctx, `[F] ${state.streakReady}`, M, hy, 9.5 * s,
        { color: CO.accent, weight: 0.16, track: 2.6 * s });
    }

    // streak-earned toast
    if (this.streakToast) {
      const t = (this.streakToast.t += dt);
      const a = t < 0.3 ? easeOut(t / 0.3) : t > 3.2 ? clamp((3.7 - t) / 0.5, 0, 1) : 1;
      if (t > 3.7) this.streakToast = null;
      else {
        const cx = this.w / 2, cy = this.h * 0.30;
        ctx.globalAlpha = a;
        const nameSize = 34 * s;
        const nw = Math.max(vwidth(this.streakToast.name, nameSize, 7 * s), 200 * s);
        const gb = ctx.createLinearGradient(cx - nw, 0, cx + nw, 0);
        gb.addColorStop(0, 'rgba(6,11,16,0)');
        gb.addColorStop(0.5, 'rgba(6,11,16,0.5)');
        gb.addColorStop(1, 'rgba(6,11,16,0)');
        ctx.fillStyle = gb;
        ctx.fillRect(cx - nw, cy - 40 * s, nw * 2, 72 * s);
        vtext(ctx, this.streakToast.sub, cx, cy - 24 * s, 10.5 * s, { color: CO.faint, weight: 0.17, track: 6 * s, align: 'center' });
        vtext(ctx, this.streakToast.name, cx, cy + 14 * s, nameSize, { color: CO.accent, weight: 0.13, track: 7 * s, align: 'center' });
        ctx.strokeStyle = 'rgba(255,196,74,0.5)'; ctx.lineWidth = 1 * s;
        ctx.beginPath(); ctx.moveTo(cx - nw * 0.5, cy + 25 * s); ctx.lineTo(cx + nw * 0.5, cy + 25 * s); ctx.stroke();
      }
    }
    ctx.restore();
  }

  _streakIcon(ctx, kind, cx, cy, r, col) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.strokeStyle = col; ctx.fillStyle = col; ctx.lineWidth = Math.max(1.2, r * 0.14);
    ctx.lineJoin = 'round';
    if (kind === 'uav') {
      ctx.beginPath();
      ctx.moveTo(-r * 0.9, 0); ctx.lineTo(r * 0.9, 0);
      ctx.moveTo(0, -r * 0.5); ctx.lineTo(0, r * 0.45);
      ctx.moveTo(-r * 0.35, r * 0.45); ctx.lineTo(r * 0.35, r * 0.45);
      ctx.stroke();
      ctx.beginPath(); ctx.arc(0, -r * 0.5, r * 0.22, 0, 6.2832); ctx.stroke();
    } else if (kind === 'strike') {
      ctx.beginPath();
      ctx.moveTo(0, -r); ctx.lineTo(r * 0.35, r * 0.2); ctx.lineTo(0, r * 0.55);
      ctx.lineTo(-r * 0.35, r * 0.2); ctx.closePath(); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(-r * 0.75, r * 0.85); ctx.lineTo(r * 0.75, r * 0.85); ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.moveTo(-r * 0.75, -r * 0.15); ctx.lineTo(r * 0.5, -r * 0.15);
      ctx.lineTo(r * 0.85, r * 0.25); ctx.lineTo(-r * 0.5, r * 0.4); ctx.closePath(); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(-r * 0.95, -r * 0.65); ctx.lineTo(r * 0.6, -r * 0.65); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(-r * 0.1, -r * 0.65); ctx.lineTo(-r * 0.1, -r * 0.2); ctx.stroke();
    }
    ctx.restore();
  }

  /* -------------------------------------------------------- objective --- */

  _drawObjective(ctx, dt) {
    if (!this.objective) return;
    const o = this.objective;
    o.t += dt;
    const s = this.s, cx = this.w / 2;
    const inT = easeOut(clamp(o.t / 0.5, 0, 1));
    const outT = clamp((o.dur - o.t) / 0.6, 0, 1);
    if (o.t > o.dur) { this.objective = null; return; }
    const a = inT * outT;
    const y = 138 * s + (1 - inT) * -14 * s;
    ctx.save();
    ctx.globalAlpha = a;
    const tw = vwidth(o.title, 20 * s, 6 * s);
    const bwid = Math.max(tw + 64 * s, 300 * s);
    chamfer(ctx, cx - bwid / 2, y - 22 * s, bwid, 46 * s, 12 * s, 0b1111);
    const g = ctx.createLinearGradient(cx - bwid / 2, 0, cx + bwid / 2, 0);
    g.addColorStop(0, 'rgba(6,11,16,0)');
    g.addColorStop(0.5, 'rgba(6,11,16,0.55)');
    g.addColorStop(1, 'rgba(6,11,16,0)');
    ctx.fillStyle = g; ctx.fill();
    ctx.strokeStyle = 'rgba(255,196,74,0.28)'; ctx.lineWidth = 1 * s; ctx.stroke();
    // wipe-in mask on the title
    ctx.save();
    ctx.beginPath();
    ctx.rect(cx - bwid / 2, y - 24 * s, bwid * inT, 48 * s);
    ctx.clip();
    vtext(ctx, o.title, cx, y - 2 * s, 20 * s, { color: CO.hi, weight: 0.13, track: 6 * s, align: 'center' });
    ctx.restore();
    vtext(ctx, o.sub, cx, y + 16 * s, 10 * s, { color: CO.accent, weight: 0.16, track: 4.4 * s, align: 'center' });
    ctx.restore();
  }

  /* ------------------------------------------------------- scoreboard --- */

  _drawScoreboard(ctx, state, dt) {
    const target = state.scoreboard ? 1 : 0;
    this.scoreboardEase = damp(this.scoreboardEase, target, 12, dt);
    if (this.scoreboardEase < 0.01) return;
    const e = easeOut(this.scoreboardEase);
    const { w, h, s } = this;
    ctx.save();
    ctx.globalAlpha = e;
    ctx.fillStyle = 'rgba(3,6,10,0.72)';
    ctx.fillRect(0, 0, w, h);

    // Real roster. This used to fall back to a fixed fake squad whose numbers
    // never moved, on every map and in every mode.
    const all = (state.scores ?? [
      { name: 'YOU', k: this.kills, d: state.deaths ?? 0, sc: this.score, me: true },
    ]).slice().sort((a, b) => b.sc - a.sc || a.name.localeCompare(b.name));

    // In a team mode the board is two columns, mine first; otherwise one list.
    const mineTeam = state.teamScore?.mine ?? null;
    const teamed = !!mineTeam && all.some((r) => r.team);
    const rowsRaw = teamed ? all.filter((r) => r.team === mineTeam).slice(0, 6) : all.slice(0, 8);
    const rowsFoe = teamed ? all.filter((r) => r.team && r.team !== mineTeam).slice(0, 6) : [];

    const bw = 780 * s, bh = (146 + (rowsRaw.length + rowsFoe.length) * 34
      + (teamed ? 74 + 40 : 0)) * s;      // +40 for the two side headings
    const x = (w - bw) / 2, y = (h - bh) / 2 + (1 - e) * 24 * s;
    chamfer(ctx, x, y, bw, bh, 20 * s, 0b1111);
    ctx.fillStyle = 'rgba(8,13,19,0.9)'; ctx.fill();
    ctx.strokeStyle = 'rgba(190,208,224,0.2)'; ctx.lineWidth = 1 * s; ctx.stroke();

    vtext(ctx, state.modeName ?? 'TEAM DEATHMATCH', x + 26 * s, y + 34 * s, 16 * s, { color: CO.hi, weight: 0.13, track: 5 * s });
    vtext(ctx, state.mapName ?? 'AL-RASHEED', x + 26 * s, y + 52 * s, 9.5 * s, { color: CO.faint, weight: 0.16, track: 3.6 * s });

    if (teamed) {
      const ts = state.teamScore ?? { red: 0, blue: 0 };
      const ours = mineTeam === 'red' ? ts.red : ts.blue;
      const theirs = mineTeam === 'red' ? ts.blue : ts.red;
      vtext(ctx, `${ours} - ${theirs}`, x + bw - 30 * s, y + 44 * s, 22 * s,
        { color: CO.hi, weight: 0.13, track: 4 * s, align: 'right' });
      vtext(ctx, `ROUND ${state.round ?? 1}${state.roundsPerMatch ? ` / ${state.roundsPerMatch}` : ''}`,
        x + bw - 30 * s, y + 62 * s, 9.5 * s,
        { color: CO.faint, weight: 0.16, track: 3.2 * s, align: 'right' });
    }

    // Which side you are on, named. "Blue" is a colour; "DEFENDERS" is a job,
    // and it is the thing you need to know when you are looking at this board.
    const sideName = (t) => (t === 'red' ? 'ATTACKERS' : 'DEFENDERS');
    const sideCol = (t) => (t === 'red' ? CO.enemy : CO.friend);

    const rows = rowsRaw;
    const hy = y + 84 * s + (teamed ? 30 * s : 0);
    if (teamed) {
      vtext(ctx, sideName(mineTeam), x + 30 * s, hy - 20 * s, 12 * s,
        { color: sideCol(mineTeam), weight: 0.15, track: 5 * s });
    }
    const cols = [['OPERATOR', x + 30 * s, 'left']];
    // Health only for your own side. Reading the enemy's is a wallhack.
    if (teamed) cols.push(['HP', x + bw - 285 * s, 'right']);
    cols.push(['K', x + bw - 210 * s, 'right'], ['D', x + bw - 140 * s, 'right'],
      ['SCORE', x + bw - 30 * s, 'right']);
    for (const [lbl, cxx, al] of cols) {
      vtext(ctx, lbl, cxx, hy, 9.5 * s, { color: CO.faint, weight: 0.16, track: 3 * s, align: al });
    }
    ctx.strokeStyle = 'rgba(190,208,224,0.18)'; ctx.lineWidth = 1 * s;
    ctx.beginPath(); ctx.moveTo(x + 26 * s, hy + 10 * s); ctx.lineTo(x + bw - 26 * s, hy + 10 * s); ctx.stroke();

    rows.forEach((r, i) => {
      const ry = hy + 40 * s + i * 34 * s;
      const t = clamp((this.scoreboardEase - i * 0.05) / 0.5, 0, 1);
      ctx.globalAlpha = e * t;
      if (r.me) {
        ctx.fillStyle = 'rgba(112,206,255,0.10)';
        ctx.fillRect(x + 20 * s, ry - 18 * s, bw - 40 * s, 27 * s);
        ctx.fillStyle = 'rgba(112,206,255,0.85)';
        ctx.fillRect(x + 20 * s, ry - 18 * s, 2.4 * s, 27 * s);
      }
      // Only YOU are highlighted. Teammates are legible but plain — the board
      // is for finding yourself first and reading the rest second.
      const col = r.me ? CO.friend : (r.dead ? CO.dim : CO.hi);
      vtext(ctx, String(r.name).toUpperCase(), x + 30 * s + (1 - t) * 12 * s, ry, 13 * s, { color: col, weight: 0.14, track: 2.4 * s });
      if (teamed) this._drawRowHealth(ctx, r, x + bw - 285 * s, ry);
      vtext(ctx, String(r.k), x + bw - 210 * s, ry, 13 * s, { color: CO.hi, weight: 0.14, align: 'right' });
      vtext(ctx, String(r.d), x + bw - 140 * s, ry, 13 * s, { color: CO.dim, weight: 0.14, align: 'right' });
      vtext(ctx, String(r.sc), x + bw - 30 * s, ry, 13 * s, { color: CO.accent, weight: 0.14, align: 'right' });
    });

    if (teamed) {
      // Opposing side, dimmer and below a divider — you want your own team at a
      // glance, theirs on inspection.
      const dy = hy + 40 * s + rowsRaw.length * 34 * s + 6 * s;
      ctx.globalAlpha = e * 0.85;
      ctx.strokeStyle = 'rgba(190,208,224,0.14)';
      ctx.lineWidth = 1 * s;
      ctx.beginPath(); ctx.moveTo(x + 26 * s, dy); ctx.lineTo(x + bw - 26 * s, dy); ctx.stroke();
      const foeTeam = mineTeam === 'red' ? 'blue' : 'red';
      vtext(ctx, sideName(foeTeam), x + 30 * s, dy + 18 * s, 12 * s,
        { color: sideCol(foeTeam), weight: 0.15, track: 5 * s });
      rowsFoe.forEach((r, i) => {
        const ry = dy + 44 * s + i * 34 * s;
        vtext(ctx, String(r.name).toUpperCase(), x + 30 * s, ry, 13 * s,
          { color: CO.enemy, weight: 0.14, track: 2.4 * s });
        vtext(ctx, String(r.k), x + bw - 210 * s, ry, 13 * s, { color: CO.hi, weight: 0.14, align: 'right' });
        vtext(ctx, String(r.d), x + bw - 140 * s, ry, 13 * s, { color: CO.dim, weight: 0.14, align: 'right' });
        vtext(ctx, String(r.sc), x + bw - 30 * s, ry, 13 * s, { color: CO.accent, weight: 0.14, align: 'right' });
      });
    }
    ctx.restore();
  }

  /**
   * A teammate's health: a short bar with the number on it.
   *
   * The bar is what you read in the half-second the board is open; the number
   * is for when you actually want to know. Dead is stated in words rather than
   * as an empty bar, because an empty bar and a bar you cannot see are the same
   * picture.
   */
  _drawRowHealth(ctx, r, xRight, y) {
    const { s } = this;
    if (r.hp == null) return;
    if (r.dead) {
      vtext(ctx, 'DEAD', xRight, y, 10 * s,
        { color: CO.enemy, weight: 0.17, track: 2.4 * s, align: 'right' });
      return;
    }
    const max = r.maxHp || 100;
    const f = clamp(r.hp / max, 0, 1);
    const w = 54 * s, h = 9 * s;
    const x = xRight - w;
    const yy = y - h + 1 * s;

    ctx.save();
    ctx.fillStyle = 'rgba(255,255,255,0.10)';
    ctx.fillRect(x, yy, w, h);
    // Green through amber to red. Health is the one number where the colour
    // should do the reading for you.
    ctx.fillStyle = f > 0.6 ? 'rgba(126,222,142,0.92)'
      : f > 0.3 ? 'rgba(255,190,90,0.94)' : 'rgba(255,96,84,0.96)';
    ctx.fillRect(x, yy, w * f, h);
    ctx.restore();
    vtext(ctx, String(r.hp), x - 6 * s, y, 11 * s,
      { color: CO.dim, weight: 0.15, align: 'right' });
  }

  /* ----------------------------------------------------------- prompt --- */

  _drawPrompt(ctx) {
    const { w, h, s } = this;
    const cx = w / 2, cy = h / 2;
    ctx.save();
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * 0.7);
    g.addColorStop(0, 'rgba(2,5,9,0.52)');
    g.addColorStop(1, 'rgba(2,5,9,0.82)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    const t = this.time;
    const a = easeOut(clamp(t / 0.9, 0, 1));
    ctx.globalAlpha = a;

    vtext(ctx, 'MISSION', cx, cy - 74 * s, 12 * s, { color: CO.accent, weight: 0.17, track: 12 * s, align: 'center' });
    vtext(ctx, 'OPERATION', cx, cy - 24 * s, 44 * s, { color: CO.hi, weight: 0.115, track: 14 * s, align: 'center' });
    vtext(ctx, 'BLACKOUT', cx, cy + 30 * s, 44 * s, { color: CO.hi, weight: 0.115, track: 14 * s, align: 'center' });

    const lw = 210 * s;
    ctx.strokeStyle = 'rgba(255,196,74,0.55)'; ctx.lineWidth = 1.2 * s;
    ctx.beginPath();
    ctx.moveTo(cx - lw, cy + 52 * s); ctx.lineTo(cx + lw, cy + 52 * s); ctx.stroke();

    const blink = 0.55 + 0.45 * Math.sin(t * 3.4);
    vtext(ctx, 'CLICK TO DEPLOY', cx, cy + 84 * s, 14 * s, {
      color: `rgba(233,242,250,${blink})`, weight: 0.15, track: 7 * s, align: 'center',
    });

    const keys = [['WASD', 'MOVE'], ['SHIFT', 'SPRINT'], ['CTRL', 'SLIDE'], ['RMB/X', 'AIM'],
      ['R', 'RELOAD'], ['1-7', 'WEAPON'], ['G/Q', 'THROW'], ['E', 'USE']];
    const cellW = 108 * s;
    const startX = cx - (keys.length * cellW) / 2;
    for (let i = 0; i < keys.length; i++) {
      const kx = startX + i * cellW + cellW / 2;
      const ky = cy + 150 * s;
      const kt = clamp((t - 0.4 - i * 0.06) / 0.4, 0, 1);
      ctx.globalAlpha = a * kt;
      const bwid = vwidth(keys[i][0], 11 * s, 2.4 * s) + 18 * s;
      chamfer(ctx, kx - bwid / 2, ky - 15 * s, bwid, 21 * s, 4 * s, 0b1111);
      ctx.fillStyle = 'rgba(255,255,255,0.07)'; ctx.fill();
      ctx.strokeStyle = 'rgba(200,216,232,0.28)'; ctx.lineWidth = 1 * s; ctx.stroke();
      vtext(ctx, keys[i][0], kx, ky, 11 * s, { color: CO.hi, weight: 0.15, track: 2.4 * s, align: 'center' });
      vtext(ctx, keys[i][1], kx, ky + 22 * s, 9 * s, { color: CO.faint, weight: 0.16, track: 2.6 * s, align: 'center' });
    }
    ctx.restore();
  }
}

/* Exposed for the capture harness's type specimen sheet. */
HUD.vtext = vtext;
HUD.vwidth = vwidth;
HUD.VF = VF;

const STREAKS = [
  { at: 3, id: 'uav', name: 'UAV', icon: 'uav' },
  { at: 5, id: 'strike', name: 'PRECISION AIRSTRIKE', icon: 'strike' },
  { at: 7, id: 'heli', name: 'ATTACK HELO', icon: 'heli' },
];

const _fv = new THREE.Vector3();
const _fp = new THREE.Vector3();
